import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ChannelType,
  DeviceSync,
  DeviceMediaItem,
  DevicePlaylist,
  SlotSignageContent,
} from "@workspace/api-client-react";
import {
  applyBusStart,
  buildRotation,
  busForChannel,
  channelDucksOthers,
  channelSlot,
  createLocalRotationState,
  createRotationAnchor,
  duckedChannels,
  effectiveChannelGain,
  emptyBusState,
  INITIAL_WANT_PLAYING,
  isCuedReport,
  isSlotActive,
  makeEntry,
  planTimedFireAudio,
  playReasonForChannel,
  playlistById,
  readSlotBehavior,
  reportedTransportState,
  screenSlotAt,
  seededShuffle,
  shouldAutoPlayEntry,
  shouldRestartOnSync,
  syncShuffleBase,
  toQueueSnapshot,
  transportTargets,
  type ActiveSlotBehavior,
  type BusState,
  type ChannelNowPlaying,
  type ChannelTransport,
  type LocalRotationState,
  type PlayOutcome,
  type QueueEntry,
  type QueueSnapshotItem,
  type RotationAnchor,
  type SeekRebase,
} from "@workspace/player-core";
import {
  alignMusicToTimeline,
  hasSyncTimeline,
} from "@/player/lib/sync-group";
import {
  alignMusicRotationToTimeline,
  planAnnouncementTakeover,
  takeNextMusicEntry,
  takeOneShotEntry,
  type MusicRotationState,
} from "@/player/lib/channel-rotation";
import {
  DRIFT_TICK_INTERVAL_MS,
  driftLoopTick,
  isDriftTickEligible,
  planDriftSeek,
} from "@/player/lib/drift-loop";
import { recordAudioFailure } from "@/player/lib/audio-error-policy";
import { evictMediaCacheEntry } from "@/player/lib/offline-store";
import { useClockOffset } from "@/player/hooks/use-clock-offset";
import {
  resolveDeviceMedia,
  resolveDevicePlaylist,
  type MediaRefArgs,
} from "@/player/lib/resolve-media";
import { boundariesWithScreenTimeline } from "@/player/lib/screen-timeline";
import {
  BoundaryScheduler,
  TimedEventScheduler,
  getTimedEvents,
  imageDisplaySeconds,
  isForegroundIntent,
  planIntentSurfaces,
  resolveTimedEventItems,
  visualShouldMute,
  type TimedEventFire,
  type ScheduleBoundary,
} from "@/player/lib/scheduling";
import type {
  DeviceStatus,
  NowPlayingPayload,
} from "@/player/hooks/use-player-realtime";
import {
  AudioMixer,
  readCrossfadeDurationSec,
  readCrossfadeMode,
  shouldCrossfadeTransition,
} from "@/player/lib/audio-mixer";
import { planTimedAudioFire } from "@/player/lib/announcement-interrupt";
import { createMediaEventRouter } from "@/player/lib/media-event-router";
import {
  DeadItemRegistry,
  FailureBudget,
  classifyMediaError,
  collectSyncItemsById,
  remapItemsById,
} from "@/player/lib/playback-failure-policy";
import {
  isSameItemAdvance,
  recordScreenFailure,
  shouldLoopVideo,
  shouldReplayAttachedVideo,
  type ScreenSurface,
} from "@/player/lib/screen-video-policy";
import { nextBackoffMs } from "@/player/lib/reconnect-backoff";
import {
  buildChannelSnapshots,
  mediaDurationSec,
} from "@/player/lib/channel-snapshots";
import {
  isToneItemId,
  readAnnouncementTone,
  resolveToneUrl,
  shouldPrependTone,
  toneQueueEntry,
} from "@/player/lib/announcement-tone";
import {
  readOutputMuted,
  readOutputVolume,
} from "@/player/lib/output-settings";
import type { DeviceMetricSender } from "@/player/lib/player-telemetry";
import {
  emitCrossfadeFallback,
  emitPlaybackFailed,
  emitPlaybackStalled,
} from "@/player/lib/player-telemetry";
import {
  isPlayerDebugEnabled,
  playerDebugLog,
  snapshotAudioElement,
} from "@/player/lib/player-debug";
import {
  boundaryForgetsOccupancy,
  isSignage,
  nextVideoIndex,
  peekNextVideoItem,
  planVideoSync,
} from "@/player/lib/video-rotation";
import { t } from "@workspace/strings/web";

/** How long a live remote volume/mute change shields the player from
 * settings-driven re-applies (stale in-flight sync payloads). */
const REMOTE_OUTPUT_SHIELD_MS = 15_000;

/**
 * How long an operator's own choice outranks the multi-room group timeline.
 * Armed from ONE seam — playEntry with `manual` — because every operator path
 * (local next/previous/shuffle, a remote channel command, play-now for a media
 * item or a whole playlist, starting an idle channel) ends there, while every
 * rejected or automatic transition does not. Arming at the transport entry
 * points instead left play-now uncovered and armed the music grace for screen
 * skips and for commands the player went on to refuse.
 */
const MANUAL_OVERRIDE_GRACE_MS = 90_000;

/**
 * Restart a video element React will not remount (advancing landed on the
 * item already on screen, so the state update bails — see isSameItemAdvance).
 */
const replayVideoElement = (el: HTMLVideoElement | null): void => {
  if (!el) return;
  try {
    el.currentTime = 0;
  } catch {
    // Seeking can throw before metadata is loaded; play() still restarts.
  }
  void el.play().catch(() => {});
};

/**
 * The single refusal every audio-starting entry point must honor while a live
 * broadcast announcement holds the audio bus. Screen-bus-only actions are
 * never routed through this guard: the picture keeps running (muted) during a
 * live announcement.
 */
export const liveAnnouncementGuard = (
  broadcastActive: boolean,
): PlayOutcome | null =>
  broadcastActive ? { ok: false, reason: "liveAnnouncementActive" } : null;

/**
 * A multi-room alignment seek older than this is discarded rather than
 * age-adjusted — it likely predates the whole segment it was computed for;
 * the drift loop re-aligns from live data instead.
 */
export const TIMELINE_SEEK_MAX_AGE_SEC = 300;

// ---- soft resync decision cores (pure, node-tested) ----
//
// A syncVersion change fingerprints the device's content as a whole, not the
// playing channel's: an edit to the ads playlist must not hard-cut the music
// bed. These pure planners let the sync effect apply a new payload while
// keeping everything the payload still carries; the hook cuts only what the
// planners say did not survive. The operator's explicit resync command keeps
// the full hard reset (see resync()).

/**
 * True while the payload carries the entry's channel at all: the /device
 * hydrators filter plan.channels to the device's enabled channels (and to
 * nothing on an entitlement lapse), so a missing channel means it was
 * switched off, not merely idle.
 */
const channelInSync = (
  sync: DeviceSync | null,
  channelType: ChannelType,
): boolean =>
  Boolean(sync?.plan?.channels?.some((ch) => ch.channelType === channelType));

/** True when the payload still carries this queued entry's content. */
const entrySurvivesSync = (
  sync: DeviceSync | null,
  entry: QueueEntry,
): boolean => {
  if (isToneItemId(entry.item.id)) return true;
  // Manual one-offs and asset-backed entries carry no playlist: their item
  // was resolved outside the payload (or pinned by the slot) and stays
  // playable until its presigned URL expires. They survive any content edit;
  // only their channel going off the payload cuts them. Operator-started
  // playlists (origin:"manual") are judged the same way — their playlist may
  // not even be in the device payload (fetched on demand from the library),
  // so a payload lookup would wrongly drop the entire remaining queue.
  if (!entry.playlistId || entry.origin === "manual") {
    return channelInSync(sync, entry.channelType);
  }
  const playlist = playlistById(sync, entry.playlistId);
  return Boolean(playlist?.items.some((item) => item.id === entry.item.id));
};

/**
 * True when a scheduled (non-interrupt) entry is still what its channel's
 * active slot plays under the new payload: same playlist occupying the slot
 * and the on-air item still in it (or the same pinned media asset).
 */
const scheduledEntrySurvives = (
  sync: DeviceSync | null,
  entry: QueueEntry,
): boolean => {
  const slot = channelSlot(sync, entry.channelType);
  if (!isSlotActive(slot)) return false;
  if (slot?.mediaAssetId) return slot.mediaAssetId === entry.item.id;
  if (!slot?.playlistId || slot.playlistId !== entry.playlistId) return false;
  const playlist = playlistById(sync, slot.playlistId);
  return Boolean(playlist?.items.some((item) => item.id === entry.item.id));
};

export interface SoftResyncPlan {
  /** Keep the on-air audio entry playing (no cut, no element restart). */
  keepCurrent: boolean;
  /** The queued interrupts still valid under the new payload. */
  queue: QueueEntry[];
}

/**
 * Decide what survives a version change: the on-air entry is kept when the
 * new payload still contains it (interrupt one-shots by their own source,
 * scheduled entries by their channel's active slot), and the interrupt queue
 * is filtered to entries the payload still carries. `keepCurrent: false` is
 * the only hard-cut signal; with nothing on air there is nothing to cut.
 */
export function planSoftResync(
  sync: DeviceSync | null,
  current: QueueEntry | null,
  queue: readonly QueueEntry[],
): SoftResyncPlan {
  const keepCurrent =
    !current ||
    (current.interrupt || current.origin === "manual"
      ? entrySurvivesSync(sync, current)
      : scheduledEntrySurvives(sync, current));
  return {
    keepCurrent,
    queue: queue.filter((entry) => entrySurvivesSync(sync, entry)),
  };
}

export interface MusicRotationRefreshPlan {
  rotation: DeviceMediaItem[];
  index: number;
  consumedItemId: string | null;
}

/**
 * Rebuild the materialized music rotation when a same-slot playlist edit
 * changed its content (items added/removed/reordered): the lazy rebuild in
 * takeNextMusicEntry only triggers on a playlist-id change, so without this a
 * long-lived slot would never pick up edits to the playlist it is playing.
 * Returns null to leave the rotation untouched: slot inactive/asset-backed,
 * playlist swapped (the lazy rebuild handles that with its correct index
 * reset), or the built order is identical (items were already re-signed by
 * the remap step). On rebuild the walk continues after the on-air item, and
 * the slot's resolved pick is marked consumed so the CB-015 anchor cannot
 * replay it on the next advance.
 */
export function planMusicRotationRefresh(
  sync: DeviceSync | null,
  state: Pick<MusicRotationState, "rotation" | "playlistId">,
  currentItemId: string | null | undefined,
): MusicRotationRefreshPlan | null {
  const slot = channelSlot(sync, "music");
  if (!isSlotActive(slot) || slot?.mediaAssetId) return null;
  const playlist = playlistById(sync, slot?.playlistId);
  if (!playlist || state.playlistId !== playlist.id) return null;
  const built = buildRotation(playlist, syncShuffleBase(sync));
  const unchanged =
    built.length === state.rotation.length &&
    built.every((item, i) => item.id === state.rotation[i]?.id);
  if (unchanged) return null;
  const currentIdx = currentItemId
    ? built.findIndex((item) => item.id === currentItemId)
    : -1;
  return {
    rotation: built,
    index: currentIdx >= 0 ? currentIdx + 1 : 0,
    consumedItemId: slot?.itemId ?? null,
  };
}

/**
 * A manual one-off (play now / add to queue) from the library. Deliberately
 * slot-free: the operator's pick is not the scheduled slot's content, so it
 * must not inherit the slot's playlist/schedule attribution (play-logs and
 * now-playing would credit the scheduled playlist) — and soft resync keys
 * manual entries on the null playlistId (see entrySurvivesSync).
 */
export const manualEntryFromMediaItem = (
  item: DeviceMediaItem,
  channelType?: ChannelType,
): QueueEntry => {
  const ct =
    channelType ??
    (item.type === "video" || item.type === "image" ? "video" : "music");
  return {
    ...makeEntry(item, playReasonForChannel(ct), null, null, true, {
      label: t("player.manualLabel"),
    }),
    channelType: ct,
  };
};

export interface PlaybackUiState {
  current: QueueEntry | null;
  previousEntry: QueueEntry | null;
  upNext: QueueEntry | null;
  /** Operator interrupt queue preview (read-only on player UI). */
  queuePreview: QueueSnapshotItem[];
  isPlaying: boolean;
  /** True only when browser autoplay policy blocked the last play() attempt. */
  autoplayBlocked: boolean;
  positionSec: number;
  durationSec: number;
  volume: number;
  muted: boolean;
  scheduleEnabled: boolean;
  /** Backdrop signage item (muted rotation on the video channel). */
  videoItem: DeviceMediaItem | null;
  /** Peek at the upcoming backdrop signage item, for preloading. */
  nextVideoItem: DeviceMediaItem | null;
  /** Web page / widget content on the video slot (renders via ZoneSignage). */
  signageContent: SlotSignageContent | null;
  videoPlaylistName: string | null;
  /** Foreground visual for av_full / av_takeover timed events. */
  foregroundVideoItem: DeviceMediaItem | null;
  foregroundVideoMuted: boolean;
  foregroundImageDisplaySec: number | null;
  /** Screen bus paused → the signage is blanked (CB-143). */
  screenPaused: boolean;
  /** Live audio diagnostics when player debug is enabled. */
  playerDebug: {
    audioSnapshot: ReturnType<typeof snapshotAudioElement>;
    audioContextState: string | null;
    stallWarning: string | null;
  };
}

export type { ChannelTransport };

export interface PlaybackControls {
  /** Resolves the real playback outcome (autoplay rejections ack honestly). */
  play: () => Promise<PlayOutcome>;
  pause: () => void;
  stop: () => void;
  /** Reports a blocked skip ("liveAnnouncementActive") instead of a silent drop. */
  next: () => PlayOutcome;
  /** Reports "historyEmpty" when there is nothing to go back to. */
  previous: () => PlayOutcome;
  setVolume: (v: number) => void;
  toggleMute: () => void;
  setMuted: (muted: boolean) => void;
  shuffleNow: () => void;
  setScheduleEnabled: (on: boolean) => void;
  setBroadcastActive: (active: boolean) => void;
  playPlaylistNow: (playlistId: string) => void;
  /** Resolves a PlayOutcome for explained drops (e.g. "emptyPlaylist"). */
  playMediaRef: (ref: MediaRefArgs) => Promise<boolean | PlayOutcome>;
  addMediaRefToQueue: (ref: MediaRefArgs) => Promise<boolean>;
  removeFromQueue: (index: number) => void;
  reorderQueue: (fromIndex: number, toIndex: number) => void;
  getQueueSnapshot: () => { queue: QueueSnapshotItem[]; current: QueueSnapshotItem | null };
  resync: () => void;
  seek: (positionSec: number) => void;
  /**
   * Apply a transport action to a single channel, enforcing the two-bus rule.
   * Always resolves a PlayOutcome: every silent drop reports
   * `{ ok: false, reason }` (short machine keys, translated on the console)
   * so remote commands never vanish without feedback.
   */
  controlChannel: (
    channelType: ChannelType,
    action: ChannelTransport,
  ) => PlayOutcome | Promise<PlayOutcome>;
  setVideoElement: (el: HTMLVideoElement | null) => void;
  setForegroundVideoElement: (el: HTMLVideoElement | null) => void;
  /** Report a still-image load failure on a screen surface (CB-122): runs the
   * same failure budget → skip/resync as a video element error. */
  reportScreenImageError: (surface: ScreenSurface) => void;
  /** Stamp a live remote volume/mute change so stale settings payloads
   * can't stomp it (see the persisted-output effect). */
  noteRemoteOutputChange: () => void;
}

export type Playback = PlaybackUiState & PlaybackControls;

interface UsePlaybackOptions {
  sync: DeviceSync | null;
  onNowPlaying?: (payload: NowPlayingPayload) => void;
  onDeviceMetric?: DeviceMetricSender;
  /** Report a device status change (e.g. "error" when the failure budget trips). */
  onStatus?: (status: DeviceStatus) => void;
}

/**
 * Two-surface playback engine: AudioProgram (music bed + interruptible
 * insertions) and VisualProgram (signage rotation + visual events). Timed
 * events and daypart boundaries from the sync payload arm local schedulers.
 */
export function usePlayback(options: UsePlaybackOptions): Playback {
  const { sync, onNowPlaying, onDeviceMetric, onStatus } = options;

  const [current, setCurrent] = useState<QueueEntry | null>(null);
  const [previousEntry, setPreviousEntry] = useState<QueueEntry | null>(null);
  const [upNext, setUpNext] = useState<QueueEntry | null>(null);
  const [queuePreview, setQueuePreview] = useState<QueueSnapshotItem[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [positionSec, setPositionSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [volume, setVolumeState] = useState(100);
  const [muted, setMutedState] = useState(false);
  const [scheduleEnabled, setScheduleEnabledState] = useState(true);
  const [videoItem, setVideoItem] = useState<DeviceMediaItem | null>(null);
  const [nextVideoItem, setNextVideoItem] = useState<DeviceMediaItem | null>(null);
  const [signageContent, setSignageContent] =
    useState<SlotSignageContent | null>(null);
  const [videoPlaylistName, setVideoPlaylistName] = useState<string | null>(null);
  const [foregroundVideoItem, setForegroundVideoItem] =
    useState<DeviceMediaItem | null>(null);
  const [foregroundVideoMuted, setForegroundVideoMuted] = useState(true);
  const [foregroundImageDisplaySec, setForegroundImageDisplaySec] = useState<
    number | null
  >(null);
  // Screen-bus pause (CB-143). Signage (a web page, menu, widget or still image)
  // has no <video> element to pause, so a "pause" on the video channel used to
  // be rejected ("noScreenMedia") and did nothing. The owner's chosen behaviour
  // is to BLANK the screen and freeze the rotation until resumed. Videos still
  // freeze on their element; only signage/stills go to the blank overlay.
  const [screenPaused, setScreenPaused] = useState(false);
  /**
   * A deliberate operator seek on the music bus. Drift correction measures
   * against this instead of the group timeline while the same item is on air —
   * without it the seek offset reads as drift and the next tick undoes it.
   * Cleared whenever the on-air item changes, so alignment resumes by itself.
   */
  const seekRebaseRef = useRef<SeekRebase | null>(null);
  const screenPausedRef = useRef(false);
  const [stallWarning, setStallWarning] = useState<string | null>(null);
  const [debugAudioSnapshot, setDebugAudioSnapshot] = useState<
    ReturnType<typeof snapshotAudioElement>
  >(null);
  const [debugAudioContextState, setDebugAudioContextState] = useState<
    string | null
  >(null);

  const stallCheckTimerRef = useRef<number | undefined>(undefined);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mixerRef = useRef<AudioMixer | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const foregroundVideoElRef = useRef<HTMLVideoElement | null>(null);

  const syncRef = useRef<DeviceSync | null>(null);
  const scheduleEnabledRef = useRef(true);
  const currentRef = useRef<QueueEntry | null>(null);
  const historyRef = useRef<QueueEntry[]>([]);
  /** Whether the audio element is actually sounding right now. */
  const isPlayingRef = useRef(false);
  /**
   * The operator's STANDING transport intent, which is a different thing from
   * isPlayingRef (see player-core transport-intent.ts). Only play/pause/stop
   * and an explicit play-now move it; every entry start reads it, so a pause
   * survives the next ad fire, daypart boundary, resync or failure retry
   * instead of being undone by the schedule seconds later. Seeded true: a
   * kiosk must resume its program on boot with nobody there to press play.
   */
  const wantPlayingRef = useRef(INITIAL_WANT_PLAYING);
  /**
   * Has the entry currently on the bus ever sounded? Reset by every entry
   * start, set by the report that tells the gateway this entry is playing —
   * so the flag means exactly "the server has seen this entry play", which is
   * what its play-log gate keys on. A silent load under a standing pause
   * leaves it false and the report goes out CUED (see isCuedReport).
   */
  const entrySoundedRef = useRef(false);

  const musicRotationRef = useRef<DeviceMediaItem[]>([]);
  const musicIndexRef = useRef(0);
  // CB-015: anchors musicIndexRef to the server-resolved item (ResolvedSlot.
  // itemId) once per slot occupancy, without reordering musicRotationRef —
  // that array's exact order is load-bearing for multi-room sync-group
  // alignment (see applyMusicTimelineAlignment below).
  const musicRotationAnchorRef = useRef<RotationAnchor>(createRotationAnchor());
  // Multi-room alignment seek for the NEXT music start. Stamped with the
  // device wall-clock at computation time: the offset is only valid for the
  // instant it was computed, and the next track may not start for minutes —
  // consume it age-adjusted (the timeline keeps moving) or not at all.
  const timelineSeekRef = useRef<{ seekSec: number; computedAtMs: number } | null>(
    null,
  );

  // Marshal the four music-rotation refs into the mutable state bag the
  // extracted controllers (channel-rotation.ts) operate on, then write any
  // mutations back. The refs stay authoritative — other consumers (drift
  // loop, shuffleNow, resync, the sync-remap effect) keep reading them.
  const withMusicRotationState = useCallback(
    <T,>(fn: (state: MusicRotationState) => T): T => {
      const state: MusicRotationState = {
        rotation: musicRotationRef.current,
        index: musicIndexRef.current,
        playlistId: musicPlaylistIdRef.current,
        anchor: musicRotationAnchorRef.current,
      };
      const out = fn(state);
      musicRotationRef.current = state.rotation;
      musicIndexRef.current = state.index;
      musicPlaylistIdRef.current = state.playlistId;
      musicRotationAnchorRef.current = state.anchor;
      return out;
    },
    [],
  );

  const applyMusicTimelineAlignment = useCallback(
    (now?: Date) =>
      withMusicRotationState((state) => {
        const alignment = alignMusicRotationToTimeline(
          syncRef.current,
          state,
          now,
        );
        if (!alignment) return false;
        timelineSeekRef.current = {
          seekSec: alignment.seekSec,
          computedAtMs: Date.now(),
        };
        return true;
      }),
    [withMusicRotationState],
  );
  const musicPlaylistIdRef = useRef<string | null>(null);

  // ---- multi-room drift correction state (music bus) ----

  // Consecutive drift ticks on which the timeline's active segment was a
  // DIFFERENT item than the one on air (see drift-loop.ts): past the debounce
  // the loop escalates to a rejoin instead of waiting forever.
  const wrongItemTicksRef = useRef(0);
  // A manual transport action (next/previous/shuffle/play-playlist) means the
  // OPERATOR chose the on-air item, and the rejoin escalation must not undo
  // them ten seconds later — the same class of bug the seek rebase (#152)
  // fixed for seeks. During the grace the group timeline yields to the
  // operator; after it, group cohesion reasserts at the next mismatch.
  const manualOverrideUntilRef = useRef(0);
  // Monotonic play-attempt counter; a mismatch marks a play() rejection as
  // superseded by a newer entry (or stop) rather than a real failure.
  const playGenRef = useRef(0);

  // Server-clock offset sampling: timeline math must run on the SERVER's wall
  // clock, or local skew shows up as phantom drift on every device.
  const { offsetMs: clockOffsetMs, correctedNowDate } = useClockOffset(
    Boolean(sync),
  );
  const clockSyncedRef = useRef(false);
  clockSyncedRef.current = clockOffsetMs !== null;

  /** Hysteresis carry-over for the drift policy (see drift-correction.ts). */
  const driftWasNudgingRef = useRef(false);
  /** True while a non-1 playbackRate has been applied to a mixer element. */
  const driftNudgeAppliedRef = useRef(false);
  /** Latest measured drift, surfaced in now-playing reports (null = unknown). */
  const lastDriftSecRef = useRef<number | null>(null);

  // Drop any active rate nudge and restore playbackRate=1 on BOTH mixer
  // elements: crossfade alternates the active element, so a rate left on the
  // idle leg would resurface when that element next carries a track.
  const resetDriftNudge = useCallback(() => {
    driftWasNudgingRef.current = false;
    if (!driftNudgeAppliedRef.current) return;
    driftNudgeAppliedRef.current = false;
    const mixer = mixerRef.current;
    const elements: readonly HTMLAudioElement[] = mixer
      ? mixer.getElements()
      : audioRef.current
        ? [audioRef.current]
        : [];
    for (const el of elements) {
      if (el.playbackRate !== 1) el.playbackRate = 1;
    }
  }, []);

  // CB-015: ads/announcements' standalone one-shot pulls (takeNextAd /
  // takeNextAnnouncement, used only for an operator's manual "play this
  // channel now") have no sync-group ordering to protect, so they consume
  // the shared pickRotationItem-backed fallback directly via nextRotationItem
  // rather than a materialized buildRotation array.
  const adRotationStateRef = useRef<LocalRotationState>(
    createLocalRotationState(),
  );

  const interruptQueueRef = useRef<QueueEntry[]>([]);
  const announcementPlaylistIdRef = useRef<string | null>(null);
  const announcementRotationStateRef = useRef<LocalRotationState>(
    createLocalRotationState(),
  );
  const broadcastActiveRef = useRef(false);
  const bedPausedForTakeoverRef = useRef(false);
  const takeoverActiveRef = useRef(false);

  const videoRotationRef = useRef<DeviceMediaItem[]>([]);
  const videoIndexRef = useRef(0);
  const videoPlaylistIdRef = useRef<string | null>(null);
  // Per-entry still-image duration of the active video slot (null when the
  // rotation was started manually or the slot has no override).
  const videoImageDisplaySecRef = useRef<number | null>(null);

  // Two-bus runtime state: which channel currently occupies each bus. The audio
  // bus holds music/ads/announcements (mutually exclusive); the screen bus holds
  // the video channel. Kept in sync by playEntry/controlChannel so per-channel
  // reporting and supersede decisions reflect what is actually on air.
  const busStateRef = useRef<BusState>(emptyBusState());

  // Screen-bus audio (the foreground/takeover video's sound). The signage bed is
  // always a muted backdrop; operator video volume/mute target the screen bus.
  const screenVolumeRef = useRef(100);
  const screenMutedRef = useRef(true);
  // True while a `muteOthers` audio slot (e.g. an announcement) is on air: the
  // screen bus' video sound is ducked to silence for its duration.
  const screenDuckedRef = useRef(false);

  // Refs mirror the video item state so the now-playing reporter (which reads
  // only refs) can describe the screen bus without re-subscribing to state.
  const videoItemRef = useRef<DeviceMediaItem | null>(null);
  const foregroundVideoItemRef = useRef<DeviceMediaItem | null>(null);
  // Mirror of the on-air web-page / widget signage payload, so the reporter can
  // surface it in now-playing even though it has no media element (CB-023/027).
  const signageContentRef = useRef<SlotSignageContent | null>(null);
  // Mirrors foregroundVideoMuted so the gesture-time autoplay recovery can
  // restore the intended mute state without re-subscribing to state.
  const foregroundVideoMutedRef = useRef(true);
  // True while the foreground video is muted ONLY because unmuted autoplay
  // was blocked (see ensureForegroundPlayback) — the gesture recovery
  // retries unmuting exactly when this is set.
  const foregroundAutoplayMutedRef = useRef(false);
  const visualEventQueueRef = useRef<
    { items: DeviceMediaItem[]; event: TimedEventFire }[]
  >([]);

  const timedSchedulerRef = useRef<TimedEventScheduler | null>(null);
  const boundarySchedulerRef = useRef<BoundaryScheduler | null>(null);

  const onNowPlayingRef = useRef(onNowPlaying);
  onNowPlayingRef.current = onNowPlaying;
  const onDeviceMetricRef = useRef(onDeviceMetric);
  onDeviceMetricRef.current = onDeviceMetric;
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  // Failure budget: consecutive media errors within a rolling window. When it
  // trips (e.g. a tab resumed from sleep with every presigned URL expired),
  // the advance loop stops skip-storming and recovery goes through a resync.
  const failureBudgetRef = useRef<FailureBudget | null>(null);
  failureBudgetRef.current ??= new FailureBudget();
  // Survives resyncs on purpose: the whole point is to recognise an item that
  // keeps failing ACROSS freshly-signed URLs.
  const deadItemsRef = useRef<DeadItemRegistry | null>(null);
  deadItemsRef.current ??= new DeadItemRegistry();
  const failureRetryTimerRef = useRef<number | undefined>(undefined);
  const failureRetryBackoffRef = useRef(0);
  const lastResyncRequestAtRef = useRef(0);

  // Screen-bus twin of the audio failure budget: signage/foreground video
  // errors are counted separately because the two buses fail independently
  // (see screen-video-policy for the advance/halt decision).
  const screenFailureBudgetRef = useRef<FailureBudget | null>(null);
  screenFailureBudgetRef.current ??= new FailureBudget();
  const screenFailureRetryTimerRef = useRef<number | undefined>(undefined);
  const screenFailureRetryBackoffRef = useRef(0);

  // Whether the signage video element should loop (single-item rotation).
  // Recorded in a ref because the element often mounts AFTER the state change
  // that decided it: setVideoElement reads the ref at mount time.
  const videoLoopRef = useRef(false);

  // Ask use-device-sync (via player.tsx wiring) for a fresh payload — the fast
  // path to new presigned URLs after a media failure. Throttled so an error
  // burst doesn't stack refetches; mirrors the schedule-updated event channel.
  const requestForceResync = useCallback(() => {
    const now = Date.now();
    if (now - lastResyncRequestAtRef.current < 5_000) return;
    lastResyncRequestAtRef.current = now;
    window.dispatchEvent(new CustomEvent("omnimedia:force-resync"));
  }, []);

  const debugMetric = useCallback(
    (category: string, data: Record<string, unknown> = {}) => {
      playerDebugLog(category, data, onDeviceMetricRef.current);
    },
    [],
  );

  const scheduleStallCheck = useCallback(
    (el: HTMLAudioElement) => {
      if (stallCheckTimerRef.current) {
        window.clearTimeout(stallCheckTimerRef.current);
      }
      const t0 = el.currentTime;
      stallCheckTimerRef.current = window.setTimeout(() => {
        stallCheckTimerRef.current = undefined;
        if (!currentRef.current || !isPlayingRef.current) return;
        const active = audioRef.current;
        if (!active || active.paused) return;
        const advanced = active.currentTime - t0;
        const ctxState = mixerRef.current?.getContextState() ?? null;
        if (ctxState === "suspended" || (advanced < 0.25 && active.readyState >= 2)) {
          const reason =
            ctxState === "suspended"
              ? t("playerUi.playback.audioSuspended")
              : t("playerUi.playback.stalled");
          setStallWarning(reason);
          setAutoplayBlocked(true);
          const detail = {
            reason,
            ctxState,
            advanced,
            ...snapshotAudioElement(active),
          };
          debugMetric("playback.stalled", detail);
          emitPlaybackStalled(onDeviceMetricRef.current, detail);
        }
      }, 2500);
    },
    [debugMetric],
  );

  const volumeRef = useRef(100);
  const mutedRef = useRef(false);
  // Per-schedule volume (0–100) of the audio entry currently on air. Composed
  // with the device global volume to form the effective output gain.
  const currentSlotVolumeRef = useRef(100);
  // True while a `muteOthers` slot is on air and the audio bus is a "victim"
  // (i.e. the on-air entry is NOT the one that requested muteOthers): the audio
  // bed is ducked to silence until the dominating slot ends.
  const audioDuckedRef = useRef(false);
  const reportDebounceRef = useRef<number | undefined>(undefined);
  const lastPositionReportAtRef = useRef(0);

  // recomputeDucking is declared below; a ref lets these mirror effects (which
  // run on every video-item change) re-evaluate ducking without a TDZ.
  const recomputeDuckingRef = useRef<() => void>(() => {});

  // Mirror video item state into refs for the ref-only now-playing reporter.
  useEffect(() => {
    videoItemRef.current = videoItem;
    busStateRef.current.screen = videoItem ? "video" : busStateRef.current.screen;
    recomputeDuckingRef.current();
  }, [videoItem]);
  useEffect(() => {
    foregroundVideoItemRef.current = foregroundVideoItem;
    recomputeDuckingRef.current();
  }, [foregroundVideoItem]);
  useEffect(() => {
    signageContentRef.current = signageContent;
  }, [signageContent]);

  // ---- audio-bus gain (global volume × per-schedule slot volume × duck) ----

  // The 0–1 base gain for the audio element: device global volume scaled by the
  // active slot's per-schedule volume, collapsed to 0 when muted. The mixer's
  // slot-gain duck multiplier (set separately) handles `muteOthers`.
  const computeAudioBaseGain = useCallback(
    () =>
      effectiveChannelGain(
        volumeRef.current,
        currentSlotVolumeRef.current,
        mutedRef.current,
      ),
    [],
  );

  // Push the current effective gain to the live audio surface. The mixer owns
  // the single output sink (its GainNode when Web Audio is wired, else the
  // element's own volume) and folds the duck in as a slot-gain factor — so we
  // must NOT also write audioRef.volume here: once Web Audio is live the element
  // volume still multiplies the gain node and squares the level (CB-141 "volume
  // stuck low"). Only the no-mixer fallback writes the element directly.
  const applyAudioGain = useCallback(() => {
    const base = computeAudioBaseGain();
    const mixer = mixerRef.current;
    if (mixer) {
      mixer.setVolume(base);
      mixer.setSlotGain(audioDuckedRef.current ? 0 : 1);
    } else if (audioRef.current) {
      audioRef.current.volume = audioDuckedRef.current ? 0 : base;
    }
  }, [computeAudioBaseGain]);

  // Ref so callbacks declared before `applyScreenAudio` can re-evaluate the
  // screen-bus mute (set further down) without a forward reference / TDZ.
  const applyScreenAudioRef2 = useRef<() => void>(() => {});

  // Derive which buses must be ducked from the slots actually on air. Only two
  // surfaces ever sound at once here: the single audio element and the video
  // (screen) bus. `duckedChannels` decides who goes silent when some on-air slot
  // sets `muteOthers` — the dominating slot keeps playing; the others duck.
  const recomputeDucking = useCallback(() => {
    const s = syncRef.current;
    const active: ActiveSlotBehavior[] = [];

    // Audio bus: the channel of the entry currently on the shared audio element.
    // The plan slot carries the server-stamped muteOthers, but it's a poll-time
    // snapshot — an announcement/ad playing via a timed fire is usually NOT the
    // channel's `now` slot yet. The channel ducking rule (the same policy the
    // server stamps from) covers that gap: whatever is audibly on the audio bus
    // ducks per its channel.
    const cur = currentRef.current;
    if (cur && busForChannel(cur.channelType) === "audio") {
      const slot = channelSlot(s, cur.channelType);
      active.push({
        channelType: cur.channelType,
        muteOthers:
          readSlotBehavior(slot).muteOthers ||
          channelDucksOthers(cur.channelType),
      });
    }

    // Screen bus: the video channel, when a video item is on air with audio.
    // The active screen-timeline window (not the poll-time now snapshot)
    // describes what is actually on screen.
    const videoItem = videoItemRef.current ?? foregroundVideoItemRef.current;
    if (videoItem) {
      const videoSlot = screenSlotAt(s, Date.now());
      active.push({
        channelType: "video",
        muteOthers: readSlotBehavior(videoSlot).muteOthers,
      });
    }

    const ducked = duckedChannels(active);
    const audioDucked = active.some(
      (a) => busForChannel(a.channelType) === "audio" && ducked.has(a.channelType),
    );
    const videoDucked = ducked.has("video");

    if (audioDucked !== audioDuckedRef.current) {
      audioDuckedRef.current = audioDucked;
      applyAudioGain();
    }
    if (videoDucked !== screenDuckedRef.current) {
      screenDuckedRef.current = videoDucked;
      applyScreenAudioRef2.current();
    }
  }, [applyAudioGain]);
  recomputeDuckingRef.current = recomputeDucking;

  // ---- now-playing reporting ----

  const refreshQueueUi = useCallback(() => {
    const q = interruptQueueRef.current;
    setUpNext(q[0] ?? null);
    setQueuePreview(q.slice(0, 8).map(toQueueSnapshot));
  }, []);

  // The snapshot-building rules live in channel-snapshots.ts (pure, node-
  // tested); this wrapper only gathers the live refs/elements into sources.
  const buildChannelSnapshotsFromRefs = useCallback(
    (deviceState: "playing" | "paused" | "stopped"): ChannelNowPlaying[] =>
      buildChannelSnapshots(
        {
          sync: syncRef.current,
          current: currentRef.current,
          audio: audioRef.current,
          volume: volumeRef.current,
          muted: mutedRef.current,
          videoItem: videoItemRef.current,
          foregroundVideoItem: foregroundVideoItemRef.current,
          signageContent: signageContentRef.current,
          screenEl: foregroundVideoElRef.current ?? videoElRef.current,
          screenVolume: screenVolumeRef.current,
          screenMuted: screenMutedRef.current,
          screenPaused: screenPausedRef.current,
        },
        deviceState,
      ),
    [],
  );

  const report = useCallback(
    (state: "playing" | "paused" | "stopped") => {
      const entry = currentRef.current;
      const audio = audioRef.current;
      // A silently loaded entry (a boundary, resync or drift rejoin pulling the
      // next item under a standing pause) is PAUSED with an item but has never
      // sounded. It still reports its item so the console keeps naming what the
      // venue is cued on, and carries `cued` so the gateway's play-log gate does
      // not count it as started — otherwise the operator's resume looks like a
      // re-report and the item (often a deferred ad) never gets a play_log row.
      const cued = isCuedReport({
        state,
        hasEntry: Boolean(entry),
        everSounded: entrySoundedRef.current,
      });
      // This frame IS the proof the entry sounded, so the flag flips here and
      // nowhere else: it can never disagree with what the server was told.
      if (state === "playing") entrySoundedRef.current = true;
      onNowPlayingRef.current?.({
        state,
        cued,
        channelType: entry?.channelType,
        playlistId: entry?.playlistId ?? null,
        title: entry?.item.title ?? null,
        artist: entry?.item.artist ?? null,
        itemId: entry?.item.id ?? null,
        durationSec: mediaDurationSec(audio, entry?.item.durationSec),
        positionSec: Math.floor(audio?.currentTime ?? 0),
        volume: mutedRef.current ? 0 : volumeRef.current,
        queue: interruptQueueRef.current.map(toQueueSnapshot),
        channels: buildChannelSnapshotsFromRefs(state),
        // Latest multi-room drift measurement (ms precision) — only attached
        // while the drift loop is actively measuring against a sync timeline.
        ...(lastDriftSecRef.current !== null
          ? { driftSec: Math.round(lastDriftSecRef.current * 1000) / 1000 }
          : {}),
      });
    },
    [buildChannelSnapshotsFromRefs],
  );

  // Active when an audio entry is playing OR the screen bus has video content.
  // The two buses are independent, so reporting must fire for either.
  const hasReportableMedia = useCallback(
    () =>
      Boolean(
        currentRef.current ||
          videoItemRef.current ||
          foregroundVideoItemRef.current ||
          signageContentRef.current,
      ),
    [],
  );

  /**
   * The device-level transport state to report. "paused" keeps naming the
   * entry the player is paused ON — reporting an idle/stopped payload instead
   * is what made the mobile Controls card read "Nothing playing" for a player
   * that was merely paused.
   */
  const currentTransportState = useCallback(() => {
    const audio = audioRef.current;
    return reportedTransportState({
      hasEntry: Boolean(currentRef.current),
      sounding: Boolean(audio && !audio.paused),
    });
  }, []);

  /** Push per-channel placeholders when the plan has active slots but audio is idle. */
  const reportScheduledChannels = useCallback(() => {
    const s = syncRef.current;
    if (!s?.plan?.channels?.length) return;
    const hasActiveSlot = s.plan.channels.some((ch) => isSlotActive(ch.now ?? null));
    if (!hasActiveSlot && !hasReportableMedia()) return;
    report(currentTransportState());
  }, [currentTransportState, hasReportableMedia, report]);

  const checkScheduleMismatch = useCallback(() => {
    const s = syncRef.current;
    if (!s || !scheduleEnabledRef.current) return;
    const cur = currentRef.current;
    for (const ch of s.plan.channels) {
      const slot = ch.now;
      if (!isSlotActive(slot ?? null) || !slot?.playlistId) continue;
      const livePlaylistId =
        cur?.channelType === ch.channelType ? (cur.playlistId ?? null) : null;
      if (livePlaylistId === slot.playlistId) continue;
      const payload = {
        channelType: ch.channelType,
        expectedPlaylistId: slot.playlistId,
        livePlaylistId,
        expectedSchedule: slot.scheduleName ?? null,
      };
      debugMetric("schedule.mismatch", payload);
      onDeviceMetricRef.current?.("schedule.mismatch", payload);
    }
  }, [debugMetric]);

  const scheduleReport = useCallback(() => {
    if (!hasReportableMedia()) return;
    if (reportDebounceRef.current) {
      window.clearTimeout(reportDebounceRef.current);
    }
    reportDebounceRef.current = window.setTimeout(() => {
      reportDebounceRef.current = undefined;
      if (!hasReportableMedia()) return;
      // Device-level state tracks the audio bus; with no audio entry it reflects
      // a stopped audio summary while the channels[] array carries screen state.
      report(currentTransportState());
    }, 120);
  }, [currentTransportState, hasReportableMedia, report]);

  // ---- visual surface helpers ----

  const clearForegroundVisual = useCallback(() => {
    takeoverActiveRef.current = false;
    bedPausedForTakeoverRef.current = false;
    setForegroundVideoItem(null);
    foregroundVideoMutedRef.current = true;
    setForegroundVideoMuted(true);
    setForegroundImageDisplaySec(null);
  }, []);

  const applyVisualItem = useCallback(
    (
      item: DeviceMediaItem | null,
      opts: {
        foreground: boolean;
        muted: boolean;
        imageDisplayDurationSec?: number | null;
        playlistName?: string | null;
      },
    ) => {
      if (opts.foreground) {
        takeoverActiveRef.current = true;
        // Re-applying the on-screen item (repeat fire, duplicate queue
        // entries) bails in React without a remount — restart the element
        // imperatively so the video actually plays again.
        if (
          item?.type === "video" &&
          isSameItemAdvance(foregroundVideoItemRef.current?.id, item.id)
        ) {
          replayVideoElement(foregroundVideoElRef.current);
        }
        setForegroundVideoItem(item);
        foregroundVideoMutedRef.current = opts.muted;
        setForegroundVideoMuted(opts.muted);
        setForegroundImageDisplaySec(
          item?.type === "image"
            ? imageDisplaySeconds(item, opts.imageDisplayDurationSec)
            : null,
        );
      } else {
        setVideoItem(item);
        if (opts.playlistName !== undefined) {
          setVideoPlaylistName(opts.playlistName);
        }
      }
    },
    [],
  );

  const advanceVisualEvent = useCallback(() => {
    const queue = visualEventQueueRef.current;
    if (queue.length === 0) {
      clearForegroundVisual();
      return;
    }
    const next = queue.shift()!;
    const item = next.items[0] ?? null;
    if (!item) {
      advanceVisualEvent();
      return;
    }
    const muted = visualShouldMute(next.event.playbackIntent);
    const foreground = isForegroundIntent(next.event.playbackIntent);
    applyVisualItem(item, {
      foreground,
      muted,
      imageDisplayDurationSec: next.event.imageDisplayDurationSec,
    });
    if (next.items.length > 1) {
      visualEventQueueRef.current.unshift({
        event: next.event,
        items: next.items.slice(1),
      });
    }
  }, [applyVisualItem, clearForegroundVisual]);

  // ---- queue selection (AudioProgram) ----

  // Selection cores live in channel-rotation.ts (pure, node-tested); the hook
  // only marshals its refs in and out.
  const takeNextMusic = useCallback(
    (): QueueEntry | null =>
      withMusicRotationState((state) =>
        // Timeline math must run on the server-corrected clock, or local skew
        // mis-picks the active segment on a rebuilt rotation.
        takeNextMusicEntry(syncRef.current, state, correctedNowDate()),
      ),
    [withMusicRotationState, correctedNowDate],
  );

  const takeNextAd = useCallback(
    (): QueueEntry | null =>
      takeOneShotEntry(syncRef.current, "ads", adRotationStateRef.current),
    [],
  );

  // Pull the next announcement as a standalone interrupt entry, used when an
  // operator starts the announcements channel directly (vs. the schedule-driven
  // syncAnnouncements path that bulk-unshifts the whole rotation).
  const takeNextAnnouncement = useCallback(
    (): QueueEntry | null =>
      takeOneShotEntry(
        syncRef.current,
        "announcements",
        announcementRotationStateRef.current,
      ),
    [],
  );

  const nextEntry = useCallback((): QueueEntry | null => {
    if (interruptQueueRef.current.length > 0) {
      return interruptQueueRef.current.shift() ?? null;
    }
    return takeNextMusic();
  }, [takeNextMusic]);

  // ---- transport ----

  const playEntry = useCallback(
    (
      entry: QueueEntry | null,
      opts?: {
        /** Operator transport (next/previous/shuffle/play-now) → hard cut even
         * in DJ mode (CB-142), and it lifts a pause. */
        manual?: boolean;
        /**
         * Whether this start asks for sound. Defaults to the standing operator
         * intent, so every scheduler-driven start (timed fire, boundary, sync,
         * drift rejoin, failure retry) inherits a pause automatically; explicit
         * play-now paths pass true.
         */
        autoPlay?: boolean;
      },
    ) => {
      // Every invocation — including stop (null) — supersedes any in-flight
      // play(); its eventual AbortError must be recognised as stale, not
      // reported as a playback failure.
      playGenRef.current += 1;
      // The operator chose this entry, so the group timeline must not pull it
      // back for the next MANUAL_OVERRIDE_GRACE_MS. Reached from every manual
      // path and from none of the automatic ones.
      if (opts?.manual) {
        manualOverrideUntilRef.current = Date.now() + MANUAL_OVERRIDE_GRACE_MS;
      }
      // A (re)start ends the operator's seek: drop the rebase so group
      // timeline alignment resumes by itself at the next boundary. The clear
      // is unconditional, including when the SAME item returns to air:
      // rebasedDriftSec matches on itemId with no TTL, so a surviving rebase
      // measured the fresh play against a position from minutes ago. A seek
      // never routes through here, so nothing legitimate is lost.
      seekRebaseRef.current = null;
      const audio = audioRef.current;
      if (!audio) return;
      // Track transition: any drift nudge belongs to the outgoing track, so
      // both mixer elements return to rate 1 before the next one starts.
      resetDriftNudge();
      lastDriftSecRef.current = null;
      const prev = currentRef.current;
      if (prev) {
        historyRef.current.push(prev);
        if (historyRef.current.length > 50) historyRef.current.shift();
        setPreviousEntry(prev);
      }
      currentRef.current = entry;
      setCurrent(entry);
      // A new occupant of the bus has sounded nothing yet (the start below may
      // be silent). Every report until it actually plays goes out cued.
      entrySoundedRef.current = false;
      refreshQueueUi();

      const transitionPayload = {
        channelType: entry?.channelType ?? null,
        reason: entry?.reason ?? "stop",
        title: entry?.item?.title ?? null,
        playlistId: entry?.playlistId ?? null,
      };
      debugMetric("playback.transition", transitionPayload);
      onDeviceMetricRef.current?.("playback.transition", transitionPayload);

      // THE sticky-pause seam. Every start of an audio entry — scheduler-
      // driven or operator-driven — resolves here, so a paused player loads
      // what the schedule says (resuming plays the right thing) and emits no
      // sound until the operator asks for it.
      const autoPlay = shouldAutoPlayEntry({
        requested: opts?.autoPlay ?? wantPlayingRef.current,
        wantPlaying: wantPlayingRef.current,
        broadcastActive: broadcastActiveRef.current,
        manual: opts?.manual ?? false,
      });

      if (!entry) {
        busStateRef.current = { ...busStateRef.current, audio: null };
        mixerRef.current?.pause();
        audio.pause();
        audio.removeAttribute("src");
        setIsPlaying(false);
        isPlayingRef.current = false;
        setPositionSec(0);
        setDurationSec(0);
        // No audio on air: drop any duck this bus imposed and restore the
        // screen-bus audio that a `muteOthers` slot had silenced.
        currentSlotVolumeRef.current = 100;
        audioDuckedRef.current = false;
        recomputeDucking();
        report("stopped");
        return;
      }

      // Everything that plays through the shared audio element occupies the audio
      // bus; record the occupant so per-channel reporting and supersede decisions
      // reflect what is actually on air. (The single audio element structurally
      // enforces mutual exclusion on this bus.)
      if (busForChannel(entry.channelType) === "audio") {
        busStateRef.current = applyBusStart(
          busStateRef.current,
          entry.channelType,
        ).next;
      }
      // Per-schedule volume for this entry's channel composes with the device
      // global volume to form the effective output gain (see applyAudioGain).
      currentSlotVolumeRef.current = readSlotBehavior(
        channelSlot(syncRef.current, entry.channelType),
      ).volume;
      // A new entry on the audio bus re-evaluates ducking before playing: this
      // entry may be a `muteOthers` slot that should silence the screen bus, or
      // it may replace one (lifting a duck the previous entry imposed).
      audioDuckedRef.current = false;
      recomputeDucking();
      const vol = computeAudioBaseGain();
      const mixer = mixerRef.current;
      const settings = syncRef.current?.device?.settings;
      // Intent comes from settings + the transition alone — NOT from
      // mixer.canCrossfade(): Web Audio is wired lazily inside mixer.play()
      // on the first crossfade-eligible transition, so gating on readiness
      // here would deadlock (readiness only ever arrives by asking). When
      // wiring fails, mixer.play() falls back to the standard path and the
      // post-play check below emits the web-audio-unavailable metric.
      const shouldCrossfade = shouldCrossfadeTransition({
        hasMixer: Boolean(mixer),
        mode: readCrossfadeMode(settings),
        prevReason: prev?.reason,
        nextReason: entry.reason,
        interrupt: entry.interrupt,
        // Operator transport (next/previous/shuffle/play-now) hard-cuts even in
        // DJ mode — a person pressing "next" wants the track NOW (CB-142).
        manual: opts?.manual,
      });
      // Consume the multi-room alignment seek, age-adjusted: the offset was
      // computed at sync time but this track may start minutes later, and the
      // timeline kept moving meanwhile — applying the raw value started the
      // track at an audibly wrong point which the drift loop then had to
      // yank. Music-only, both for the clear AND the apply (an interleaved
      // ad/announcement must never inherit the music bus's seek). A silent
      // load leaves the stashed seek alone: it belongs to the start that
      // actually joins the group timeline (its own age guard discards it if
      // the pause outlasts it).
      let seekAt: number | null = null;
      if (autoPlay && timelineSeekRef.current != null && entry.reason === "music") {
        const { seekSec, computedAtMs } = timelineSeekRef.current;
        timelineSeekRef.current = null;
        const ageSec = Math.max(0, (Date.now() - computedAtMs) / 1000);
        if (ageSec <= TIMELINE_SEEK_MAX_AGE_SEC) {
          seekAt = seekSec + ageSec;
        }
      }

      // A start that actually sounds IS the standing intent from here on, so
      // an operator play-now while paused resumes the venue for good rather
      // than for one item (native parity).
      if (autoPlay) wantPlayingRef.current = true;

      // Rapid transport (an operator mashing next) supersedes this play()
      // before it settles; the browser rejects the stale promise with
      // AbortError. That rejection is bookkeeping, not a failure — the newer
      // playEntry owns the element — so the catch below must not flag the
      // kiosk blocked or emit playback_failed for it.
      const playGen = playGenRef.current;
      const startPlayback = async (): Promise<boolean> => {
        debugMetric("playEntry.start", {
          title: entry.item.title,
          urlHost: (() => {
            try {
              return new URL(entry.item.url).host;
            } catch {
              return null;
            }
          })(),
          crossfade: shouldCrossfade,
          autoPlay,
        });
        let el: HTMLAudioElement;
        if (mixer) {
          el = await mixer.play(entry.item.url, {
            crossfade: shouldCrossfade,
            durationSec: readCrossfadeDurationSec(settings),
            volume: vol,
            autoPlay,
          });
          audioRef.current = el;
          if (autoPlay && !mixer.canCrossfade() && shouldCrossfade) {
            emitCrossfadeFallback(onDeviceMetricRef.current, "web-audio-unavailable");
          }
        } else {
          audio.src = entry.item.url;
          audio.volume = vol;
          if (autoPlay) await audio.play();
          el = audio;
        }
        if (!autoPlay) {
          // Loaded, silent, and on the bus: report it so the console shows a
          // PAUSED player still named with what it is paused on, rather than
          // an idle "Nothing playing" (the element's own loadedmetadata may
          // never arrive offline). entrySoundedRef is still false here, so the
          // frame goes out CUED and this entry does not claim a play it has
          // not made — the resume that finally sounds it is what gets logged.
          report("paused");
          return false;
        }
        const ctxState = mixer?.getContextState() ?? null;
        if (ctxState === "suspended") {
          await mixer?.resumeContext();
        }
        if (mixer?.getContextState() === "suspended") {
          setAutoplayBlocked(true);
          setStallWarning(t("playerUi.playback.audioSuspended"));
          debugMetric("audio.context-suspended", { ctxState });
          emitPlaybackStalled(onDeviceMetricRef.current, { ctxState });
          return true;
        }
        if (seekAt != null && seekAt > 0) {
          el.currentTime = seekAt;
          setPositionSec(Math.floor(seekAt));
        }
        setStallWarning(null);
        scheduleStallCheck(el);
        debugMetric("playEntry.ok", {
          ctxState: mixer?.getContextState() ?? null,
          ...snapshotAudioElement(el),
        });
        return false;
      };

      void startPlayback()
        .then((blocked) => {
          if (!blocked) setAutoplayBlocked(false);
        })
        .catch((err: unknown) => {
          if (playGenRef.current !== playGen) {
            debugMetric("playEntry.superseded", {
              itemId: entry.item.id,
              errName: err instanceof Error ? err.name : null,
            });
            return;
          }
          if (shouldCrossfade) {
            emitCrossfadeFallback(onDeviceMetricRef.current, "playback-error");
          }
          setIsPlaying(false);
          isPlayingRef.current = false;
          setAutoplayBlocked(true);
          const message = err instanceof Error ? err.message : String(err);
          setStallWarning(t("playerUi.playback.failed", { message }));
          debugMetric("playEntry.error", { message });
          emitPlaybackFailed(onDeviceMetricRef.current, {
            itemId: entry.item.id,
            message,
          });
        });
    },
    [
      computeAudioBaseGain,
      debugMetric,
      recomputeDucking,
      refreshQueueUi,
      report,
      resetDriftNudge,
      scheduleStallCheck,
    ],
  );

  // Returns a PlayOutcome so operator-driven skips reachable from remote
  // commands can report WHY nothing happened (a live announcement holds the
  // bus, the schedule is off, or the queue and rotation ran dry). Timer-driven
  // callers (onEnded, schedulers) ignore the result.
  const advance = useCallback(
    (opts?: { manual?: boolean }): PlayOutcome => {
      const blocked = liveAnnouncementGuard(broadcastActiveRef.current);
      if (blocked) return blocked;
      if (!scheduleEnabledRef.current) {
        playEntry(null);
        return { ok: false, reason: "nothingScheduled" };
      }
      if (takeoverActiveRef.current && bedPausedForTakeoverRef.current) {
        clearForegroundVisual();
        bedPausedForTakeoverRef.current = false;
        const audio = audioRef.current;
        // Restore the bed the takeover paused — but only up to the operator's
        // standing intent: a takeover ending must not un-pause a venue the
        // operator paused.
        if (wantPlayingRef.current && currentRef.current && audio?.src) {
          void audio.play().catch(() => setIsPlaying(false));
        }
      }
      const entry = nextEntry();
      playEntry(entry, opts);
      return entry ? { ok: true } : { ok: false, reason: "nothingScheduled" };
    },
    [clearForegroundVisual, nextEntry, playEntry],
  );

  // ---- timed events ----

  const applyTimedEventFire = useCallback(
    (event: TimedEventFire) => {
      const s = syncRef.current;
      const items = resolveTimedEventItems(s, event);
      if (items.length === 0) return;

      const plan = planIntentSurfaces(event.playbackIntent);
      const reason = playReasonForChannel(event.channelType);
      const slot = channelSlot(s, event.channelType);
      const playlist =
        event.contentType === "playlist"
          ? playlistById(s, event.playlistId)
          : null;

      const extras = {
        label: event.label,
        playbackIntent: event.playbackIntent,
        imageDisplayDurationSec: event.imageDisplayDurationSec,
      };

      if (plan.audio === "pause_bed") {
        bedPausedForTakeoverRef.current = true;
        // Mixer pause cancels an in-flight crossfade too (both legs silent).
        if (mixerRef.current) mixerRef.current.pause();
        else audioRef.current?.pause();
      }

      if (plan.audio === "play") {
        // A paused player must not be touched on the audio bus by the
        // schedule. Shared core (player-core transport-intent.ts): the
        // insertion would sound to nobody, and loading it silently would park
        // it on the bus for the whole pause — it never ends without playback,
        // so the console would name a paused AD instead of the track the
        // operator paused on, and the ad would then play stale on resume. The
        // VISUAL half below still applies: the screen bus is its own transport.
        const audioPlan = planTimedFireAudio({
          hasAudio: true,
          interrupting: true,
          busIdle: !currentRef.current,
          wantPlaying: wantPlayingRef.current,
        });
        if (!audioPlan.takeAudio) {
          debugMetric("timed_event.skipped", {
            channelType: event.channelType,
            fireAt: event.fireAt,
            reason: "transport_paused",
          });
        } else {
          // conflictPolicy: what this fire does if the same channel is already
          // playing. interrupt → play now (cut the current item); wait → defer,
          // letting the current item finish (queue without cutting in); skip →
          // drop this fire entirely (no queue, no interrupt). The decision core
          // (conflict shape + audio-item filter + one-tone-per-takeover prepend)
          // is the pure planTimedAudioFire; this hook keeps the side effects.
          const channelAlreadyPlaying =
            currentRef.current?.channelType === event.channelType &&
            isPlayingRef.current;
          const firePlan = planTimedAudioFire({
            channelType: event.channelType,
            // The fire's OWN policy (the server stamps the entry's startBehavior
            // on every timed fire). The channel's plan slot is a poll-time
            // snapshot that rarely describes this fire; payloads cached before
            // the enrichment shipped fall back to the interrupt default.
            conflictPolicy: readSlotBehavior(event).conflictPolicy,
            channelAlreadyPlaying,
            items,
            makeEntry: (it) =>
              makeEntry(it, reason, slot, playlist, reason !== "music", extras),
            tone: readAnnouncementTone(s?.device?.settings),
            toneSrc: resolveToneUrl(
              s?.device?.settings,
              s?.device?.announcementToneUrl,
            ),
            currentItemId: currentRef.current?.item.id,
            queueHeadItemId: interruptQueueRef.current[0]?.item.id,
          });
          if (firePlan.kind === "skip") {
            debugMetric("timed_event.skipped", {
              channelType: event.channelType,
              fireAt: event.fireAt,
              reason: "conflict_skip",
            });
            return;
          }
          const entries = firePlan.entries;
          if (entries.length > 0) {
            if (firePlan.kind === "defer") {
              // Defer: let the on-air item finish, then this fire plays from the
              // interrupt queue (onended → advance pulls it). No cut-in.
              interruptQueueRef.current.unshift(...entries);
              refreshQueueUi();
            } else {
              const [first, ...rest] = entries;
              interruptQueueRef.current.unshift(...rest);
              if (broadcastActiveRef.current) return;
              // No autoPlay override: a timed fire is the SCHEDULE talking, so
              // it inherits the operator's standing intent (the audioPlan gate
              // above has already dropped it outright while paused). This call
              // used to restart a paused venue on every ad/announcement fire.
              playEntry(first);
            }
          }
        }
      }

      if (plan.visual !== "unchanged") {
        const visualItems = items.filter(isSignage);
        const toPlay = visualItems.length > 0 ? visualItems : items;
        const muted = visualShouldMute(event.playbackIntent);
        const foreground = isForegroundIntent(event.playbackIntent);

        if (toPlay.length === 1) {
          applyVisualItem(toPlay[0] ?? null, {
            foreground,
            muted,
            imageDisplayDurationSec: event.imageDisplayDurationSec,
            playlistName: playlist?.name ?? slot?.playlistName ?? null,
          });
        } else if (toPlay.length > 1) {
          visualEventQueueRef.current.push({ event, items: toPlay });
          advanceVisualEvent();
        }
      }
    },
    [advanceVisualEvent, applyVisualItem, debugMetric, playEntry],
  );

  // ---- announcement interruption ----

  const syncAnnouncements = useCallback(() => {
    // Decision core (occupancy change detection, rotation build, one-tone-per-
    // takeover prepend) lives in channel-rotation.ts; this applier only queues.
    const plan = planAnnouncementTakeover({
      sync: syncRef.current,
      previousSlotKey: announcementPlaylistIdRef.current,
      currentItemId: currentRef.current?.item.id,
      queueHeadItemId: interruptQueueRef.current[0]?.item.id,
    });
    switch (plan.kind) {
      case "inactive":
        announcementPlaylistIdRef.current = null;
        return false;
      case "unchanged":
        return false;
      case "empty":
        announcementPlaylistIdRef.current = plan.slotKey;
        return false;
      case "queue":
        interruptQueueRef.current.unshift(...plan.entries);
        announcementPlaylistIdRef.current = plan.slotKey;
        return true;
    }
  }, []);

  // ---- video signage surface (VisualProgram bed) ----

  // Peek the item the backdrop rotation will show next (for preloading).
  const peekNextVideo = useCallback(
    (): DeviceMediaItem | null =>
      peekNextVideoItem(videoRotationRef.current, videoIndexRef.current),
    [],
  );

  // Record the signage loop decision and push it to a live element. A
  // single-item rotation must loop on the element itself: advancing lands on
  // the same item object, React bails, and no remount ever restarts playback.
  const applyVideoLoop = useCallback((loop: boolean) => {
    videoLoopRef.current = loop;
    const el = videoElRef.current;
    if (el && el.loop !== loop) el.loop = loop;
  }, []);

  // A screen video is actually rendering again (or its rotation is being
  // replaced): the failure streak is over. Drop the budget, the backoff, and
  // any pending halt-retry — a stale timer firing after a fresh sync would
  // advanceVideo() past the first item of the recovered rotation.
  const noteScreenPlaybackHealthy = useCallback(() => {
    screenFailureBudgetRef.current?.reset();
    screenFailureRetryBackoffRef.current = 0;
    if (screenFailureRetryTimerRef.current !== undefined) {
      window.clearTimeout(screenFailureRetryTimerRef.current);
      screenFailureRetryTimerRef.current = undefined;
    }
  }, []);

  const syncVideo = useCallback(() => {
    // Decision core (screen-timeline window, occupancy change, signage
    // payload) lives in video-rotation.ts; this applier owns the refs, React
    // state, and element side effects. The signage ref is set synchronously
    // (not just via the mirror effect) so the reportScheduledChannels() call
    // that follows this syncVideo() in the same pass sees the on-air signage.
    const plan = planVideoSync(
      syncRef.current,
      Date.now(),
      videoPlaylistIdRef.current,
    );
    signageContentRef.current = plan.signage;
    setSignageContent(plan.signage);
    if (plan.kind === "inactive") {
      videoPlaylistIdRef.current = null;
      videoRotationRef.current = [];
      videoIndexRef.current = 0;
      videoImageDisplaySecRef.current = null;
      noteScreenPlaybackHealthy();
      applyVideoLoop(false);
      setNextVideoItem(null);
      if (!takeoverActiveRef.current) {
        setVideoItem(null);
        setVideoPlaylistName(null);
      }
      return;
    }
    if (plan.rotationChanged) {
      videoPlaylistIdRef.current = plan.slotKey;
      videoRotationRef.current = plan.items;
      videoIndexRef.current = 0;
      // Fresh rotation: cancel any halt-retry armed against the old one.
      noteScreenPlaybackHealthy();
    }
    videoImageDisplaySecRef.current = plan.imageDisplaySec;
    applyVideoLoop(shouldLoopVideo(videoRotationRef.current.length));
    setVideoPlaylistName(plan.playlistName);
    if (!takeoverActiveRef.current) {
      const next = videoRotationRef.current[videoIndexRef.current] ?? null;
      // Same on-air item (by id) → keep the CURRENT object. Every sync 200
      // remaps the rotation onto freshly-presigned item objects, and pushing
      // the new object into state rewrites the live element's src — the clip
      // visibly restarted from zero on every poll, and a still image's dwell
      // timer re-armed so it could never elapse. The fresh URL still lands in
      // the rotation array and takes effect the next time the item starts.
      if (next?.id !== videoItemRef.current?.id) {
        setVideoItem(next);
      }
    }
    setNextVideoItem(peekNextVideo());
  }, [applyVideoLoop, noteScreenPlaybackHealthy, peekNextVideo]);

  const advanceVideo = useCallback(() => {
    if (takeoverActiveRef.current) return;
    const rotation = videoRotationRef.current;
    if (rotation.length === 0) {
      applyVideoLoop(false);
      setVideoItem(null);
      setNextVideoItem(null);
      return;
    }
    videoIndexRef.current = nextVideoIndex(videoIndexRef.current, rotation.length);
    const next = rotation[videoIndexRef.current] ?? null;
    applyVideoLoop(shouldLoopVideo(rotation.length));
    // Advancing a single-item rotation (or onto a duplicate adjacent entry)
    // lands on the item already on screen: React bails on the identical
    // state, so restart the element imperatively instead of waiting for a
    // remount that never comes.
    if (
      next?.type === "video" &&
      isSameItemAdvance(videoItemRef.current?.id, next.id)
    ) {
      replayVideoElement(videoElRef.current);
    }
    setVideoItem(next);
    setNextVideoItem(peekNextVideo());
  }, [applyVideoLoop, peekNextVideo]);

  // Screen-bus twin of the audio element's onError: record the failure,
  // advance the rotation instead of freezing, and stop advancing (halt +
  // resync + backoff retry) once every candidate has failed in the window so
  // an all-broken rotation doesn't hot-loop. See screen-video-policy.
  const handleScreenVideoError = useCallback(
    (surface: ScreenSurface) => {
      const foreground = surface === "foreground";
      const el = foreground
        ? foregroundVideoElRef.current
        : videoElRef.current;
      const item = foreground
        ? foregroundVideoItemRef.current
        : videoItemRef.current;
      const classification = classifyMediaError({
        mediaErrorCode: el?.error?.code ?? null,
        urlExpiresAt: item?.urlExpiresAt ?? null,
      });
      // Same rule as the audio bus: a poisoned cached copy would defeat every
      // retry cache-first — drop it before requesting fresh URLs.
      if (item?.url) void evictMediaCacheEntry(item.url);
      // Candidates that advancing could still reach: the remaining queued
      // foreground items (plus the one on screen), or the signage rotation.
      const candidateCount = foreground
        ? 1 +
          visualEventQueueRef.current.reduce((n, q) => n + q.items.length, 0)
        : videoRotationRef.current.length;
      const { action, consecutiveFailures } = recordScreenFailure(
        screenFailureBudgetRef.current!,
        candidateCount,
      );
      emitPlaybackFailed(onDeviceMetricRef.current, {
        itemId: item?.id ?? null,
        title: item?.title ?? null,
        channelType: "video",
        surface,
        errorCode: el?.error?.code ?? null,
        classification,
        consecutiveFailures,
      });
      debugMetric("video.error", {
        surface,
        classification,
        consecutiveFailures,
      });
      // An expired presigned URL won't heal by skipping — the fix is a resync
      // for fresh URLs. Still advance below: other items may be valid.
      if (classification === "expired-url") requestForceResync();
      if (action === "advance") {
        if (foreground) advanceVisualEvent();
        else advanceVideo();
        return;
      }
      // Every plausible candidate failed inside the window.
      onStatusRef.current?.("error");
      requestForceResync();
      if (foreground) {
        // Foreground fires are one-shot takeovers: end the takeover so the
        // screen returns to the signage bed instead of freezing on a broken
        // element. A later fire (or resync) starts fresh.
        visualEventQueueRef.current = [];
        clearForegroundVisual();
        return;
      }
      if (screenFailureRetryTimerRef.current === undefined) {
        screenFailureRetryBackoffRef.current = nextBackoffMs(
          screenFailureRetryBackoffRef.current,
        );
        screenFailureRetryTimerRef.current = window.setTimeout(() => {
          screenFailureRetryTimerRef.current = undefined;
          requestForceResync();
          advanceVideo();
        }, screenFailureRetryBackoffRef.current);
      }
    },
    [
      advanceVideo,
      advanceVisualEvent,
      clearForegroundVisual,
      debugMetric,
      requestForceResync,
    ],
  );

  // Clear the screen-bus halt-retry timer on unmount (the audio lifecycle
  // effect's cleanup owns the audio-bus twin).
  useEffect(
    () => () => {
      if (screenFailureRetryTimerRef.current !== undefined) {
        window.clearTimeout(screenFailureRetryTimerRef.current);
      }
    },
    [],
  );

  const handleBoundaryTransition = useCallback(
    (boundary: ScheduleBoundary, transition: "start" | "end") => {
      const channelType = boundary.channelType;

      switch (channelType) {
        case "music":
          musicPlaylistIdRef.current = null;
          break;
        case "ads":
          adRotationStateRef.current = createLocalRotationState();
          break;
        case "video":
          // The screen bus is the ONE channel that keeps its occupancy across
          // a boundary — see boundaryForgetsOccupancy for why clearing it here
          // froze the venue's screen on a last frame.
          if (boundaryForgetsOccupancy(channelType)) {
            videoPlaylistIdRef.current = null;
          }
          syncVideo();
          break;
        case "announcements":
          announcementPlaylistIdRef.current = null;
          syncAnnouncements();
          break;
      }

      // The cached payload's plan.channels[].now is a snapshot from the LAST
      // sync — advancing against it at a daypart edge re-selects the expired
      // slot and audibly restarts the OLD program from the top. Pull fresh
      // data now (throttled; the audio bus has no time-aware slot resolver
      // like the screen bus's screenSlotAt), so the new daypart starts within
      // a fetch round-trip instead of up to a full poll interval late.
      requestForceResync();

      if (transition === "start" && channelType !== "video") {
        // Under a sync timeline, stamp the in-track seek for the NEXT music
        // start before advancing — otherwise the post-boundary track starts at
        // 0:00 while the group's timeline is mid-segment, and (pre-rejoin fix)
        // the player had no way back.
        if (channelType === "music" && hasSyncTimeline(syncRef.current)) {
          applyMusicTimelineAlignment(correctedNowDate());
        }
        advance();
      }
    },
    [
      advance,
      applyMusicTimelineAlignment,
      correctedNowDate,
      requestForceResync,
      syncAnnouncements,
      syncVideo,
    ],
  );

  // Stills rotate on a timer; video items advance via onended. A blanked
  // (paused) screen must NOT keep rotating underneath the blank (CB-143).
  useEffect(() => {
    if (screenPaused) return;
    if (videoItem?.type !== "image") return;
    if (videoRotationRef.current.length < 2) return;
    const seconds = imageDisplaySeconds(
      videoItem,
      videoImageDisplaySecRef.current,
    );
    const timer = setTimeout(() => advanceVideo(), seconds * 1000);
    return () => clearTimeout(timer);
  }, [videoItem, advanceVideo, screenPaused]);

  useEffect(() => {
    if (screenPaused) return;
    if (foregroundVideoItem?.type !== "image") return;
    const seconds = imageDisplaySeconds(
      foregroundVideoItem,
      foregroundImageDisplaySec,
    );
    const timer = setTimeout(() => advanceVisualEvent(), seconds * 1000);
    return () => clearTimeout(timer);
  }, [foregroundVideoItem, foregroundImageDisplaySec, advanceVisualEvent, screenPaused]);

  // ---- schedulers ----

  // Latest-callback mirrors so the schedulers can live across payloads (see
  // below) without closing over stale callbacks.
  const applyTimedEventFireRef = useRef(applyTimedEventFire);
  applyTimedEventFireRef.current = applyTimedEventFire;
  const handleBoundaryTransitionRef = useRef(handleBoundaryTransition);
  handleBoundaryTransitionRef.current = handleBoundaryTransition;
  const correctedNowMsRef = useRef<() => number>(() => Date.now());
  correctedNowMsRef.current = () => correctedNowDate().getTime();

  useEffect(() => {
    if (!sync) return;

    // ONE scheduler instance per hook lifetime, RE-armed on each payload.
    // Rebuilding the instance per payload threw away queuedWhileBroadcast —
    // any announcement/ad deferred during a live broadcast silently vanished
    // whenever a sync landed mid-broadcast (broadcasts routinely outlast the
    // poll interval). Timers are armed on the server-corrected clock: fireAt/
    // boundary instants are server-stamped, so the raw device clock silently
    // discarded every fire within the skew on a fast device ("the schedule
    // is ignored") and ran minutes late on a slow one.
    let timed = timedSchedulerRef.current;
    if (!timed) {
      const scheduler: TimedEventScheduler = new TimedEventScheduler({
        onFire: (event) => applyTimedEventFireRef.current(event),
        isBroadcastActive: () => broadcastActiveRef.current,
        isAdStillPlaying: () => {
          const cur = currentRef.current;
          return Boolean(cur?.reason === "ad" && isPlayingRef.current);
        },
        queueAfterBroadcast: (event) => {
          scheduler.queueForBroadcast(event);
        },
        nowMs: () => correctedNowMsRef.current(),
        onStaleFire: (event, lateByMs) => {
          debugMetric("timed_event.skipped", {
            channelType: event.channelType,
            fireAt: event.fireAt,
            lateByMs,
            reason: "stale_fire",
          });
        },
      });
      timed = scheduler;
      timedSchedulerRef.current = timed;
    }
    timed.arm(getTimedEvents(sync), correctedNowMsRef.current(), (skipped) => {
      for (const event of skipped) {
        debugMetric("timed_event.skipped", {
          channelType: event.channelType,
          fireAt: event.fireAt,
          reason: "late_start",
        });
      }
    });

    let boundary = boundarySchedulerRef.current;
    if (!boundary) {
      boundary = new BoundaryScheduler({
        onTransition: (b, transition) =>
          handleBoundaryTransitionRef.current(b, transition),
        nowMs: () => correctedNowMsRef.current(),
        onStaleBurst: () => {
          // Wake-after-suspend: replaying the burst of missed transitions
          // would advance() once per boundary. Re-derive from fresh data.
          requestForceResync();
        },
      });
      boundarySchedulerRef.current = boundary;
    }
    // Screen-timeline windows replace the video base boundaries so the screen
    // bus re-syncs at EVERY scheduled transition in the horizon (base + timed
    // signage alike), not just base-bed edges (CB-024/CB-028).
    boundary.arm(boundariesWithScreenTimeline(sync), correctedNowMsRef.current());

    return () => {
      timed.disarm();
      boundary.disarm();
    };
  }, [sync, debugMetric, requestForceResync, correctedNowDate]);

  // ---- audio element lifecycle ----

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "auto";
    audioRef.current = audio;
    const mixer = new AudioMixer(audio);
    mixerRef.current = mixer;

    // Media is loading/playing again: the failure streak is over. Drop the
    // budget, the backoff, and any pending trip-retry (a manual or scheduled
    // start already recovered playback — a delayed resync+advance would cut it).
    const notePlaybackHealthy = () => {
      failureBudgetRef.current?.reset();
      // This exact item just played, so whatever was wrong with it is over —
      // an asset uploaded after the fact heals without a reload.
      deadItemsRef.current?.clear(currentRef.current?.item.id);
      failureRetryBackoffRef.current = 0;
      if (failureRetryTimerRef.current !== undefined) {
        window.clearTimeout(failureRetryTimerRef.current);
        failureRetryTimerRef.current = undefined;
      }
    };

    const onEnded = () => advance();
    const onTime = (el: HTMLAudioElement) => {
      setPositionSec(Math.floor(el.currentTime));
      if (!currentRef.current || el.paused) return;
      const now = Date.now();
      if (now - lastPositionReportAtRef.current < 2000) return;
      lastPositionReportAtRef.current = now;
      report("playing");
    };
    const onLoaded = (el: HTMLAudioElement) => {
      notePlaybackHealthy();
      setDurationSec(
        Number.isFinite(el.duration) ? Math.floor(el.duration) : 0,
      );
      if (currentRef.current) {
        report(el.paused ? "paused" : "playing");
      }
    };
    const onPlay = () => {
      notePlaybackHealthy();
      setIsPlaying(true);
      isPlayingRef.current = true;
      setAutoplayBlocked(false);
      report("playing");
    };
    const onPause = () => {
      setIsPlaying(false);
      isPlayingRef.current = false;
      if (currentRef.current) report("paused");
    };
    const onError = (el: HTMLAudioElement) => {
      const snap = snapshotAudioElement(el);
      const entry = currentRef.current;
      // If the cached copy is what's failing (poisoned/corrupt entry), the
      // cache-first worker would keep serving it to every retry no matter
      // how fresh the resynced URL is — drop it before asking for one.
      if (entry?.item.url) void evictMediaCacheEntry(entry.item.url);
      // Decision core (tone exemption, classification, budget trip) lives in
      // audio-error-policy.ts; this handler keeps the side effects.
      const decision = recordAudioFailure({
        budget: failureBudgetRef.current!,
        isToneEntry: Boolean(entry && isToneItemId(entry.item.id)),
        mediaErrorCode: snap?.errorCode ?? null,
        urlExpiresAt: entry?.item.urlExpiresAt ?? null,
        candidateCount:
          interruptQueueRef.current.length + musicRotationRef.current.length,
        itemId: entry?.item.id ?? null,
        deadItems: deadItemsRef.current ?? undefined,
      });
      if (decision.kind === "tone-skip") {
        debugMetric("audio.tone_error", {
          ...(snap ?? {}),
          itemId: entry?.item.id,
        });
        requestForceResync();
        advance();
        return;
      }
      if (decision.kind === "skip-dead") {
        // Retired, not retried. No resync is requested: a fresh presigned URL
        // for an object that is not in the bucket is exactly the loop this
        // branch exists to break. Reported once so the failure is still
        // visible, then the channel carries on with the remaining items.
        emitPlaybackFailed(onDeviceMetricRef.current, {
          itemId: entry?.item.id ?? null,
          title: entry?.item.title ?? null,
          channelType: entry?.channelType ?? null,
          errorCode: snap?.errorCode ?? null,
          classification: decision.classification,
          consecutiveFailures: 0,
        });
        debugMetric("audio.item_retired", {
          ...(snap ?? {}),
          itemId: entry?.item.id,
          classification: decision.classification,
        });
        advance();
        return;
      }
      emitPlaybackFailed(onDeviceMetricRef.current, {
        itemId: entry?.item.id ?? null,
        title: entry?.item.title ?? null,
        channelType: entry?.channelType ?? null,
        errorCode: snap?.errorCode ?? null,
        classification: decision.classification,
        consecutiveFailures: decision.consecutiveFailures,
      });
      debugMetric("audio.error", {
        ...(snap ?? {}),
        classification: decision.classification,
        consecutiveFailures: decision.consecutiveFailures,
      });
      // An expired presigned URL won't heal by skipping — the fix is a resync
      // for fresh URLs. Still advance below: other queue items may be valid.
      if (decision.classification === "expired-url") requestForceResync();
      if (decision.kind === "halt") {
        // Every plausible candidate failed inside the window: stop the
        // skip-storm, surface an error status, and retry once after a backoff
        // (resync + advance). If that retry fails too, the budget re-trips and
        // schedules the next attempt with a longer delay.
        setIsPlaying(false);
        isPlayingRef.current = false;
        onStatusRef.current?.("error");
        requestForceResync();
        if (failureRetryTimerRef.current === undefined) {
          failureRetryBackoffRef.current = nextBackoffMs(
            failureRetryBackoffRef.current,
          );
          failureRetryTimerRef.current = window.setTimeout(() => {
            failureRetryTimerRef.current = undefined;
            requestForceResync();
            advance();
          }, failureRetryBackoffRef.current);
        }
        return;
      }
      advance();
    };

    // Crossfade alternates playback between the mixer's two elements, so the
    // same listener set is attached to BOTH and each event is routed by the
    // element's role at dispatch time: `ended` keeps reaching advance() after
    // the active element flips, and errors from the incoming crossfade leg
    // still hit the failure budget while idle-element teardown noise is
    // ignored (see media-event-router.ts).
    const router = createMediaEventRouter<HTMLAudioElement>(
      (el) => mixer.elementRole(el),
      {
        ended: onEnded,
        timeupdate: onTime,
        loadedmetadata: onLoaded,
        play: onPlay,
        pause: onPause,
        error: onError,
      },
    );
    const [elementA, elementB] = mixer.getElements();
    router.attach(elementA);
    router.attach(elementB);

    // Sync may arrive before the audio element exists; kick playback once ready.
    if (
      syncRef.current &&
      scheduleEnabledRef.current &&
      !currentRef.current
    ) {
      queueMicrotask(() => advance());
    }

    return () => {
      if (reportDebounceRef.current) {
        window.clearTimeout(reportDebounceRef.current);
        reportDebounceRef.current = undefined;
      }
      if (failureRetryTimerRef.current !== undefined) {
        window.clearTimeout(failureRetryTimerRef.current);
        failureRetryTimerRef.current = undefined;
      }
      // Detach from BOTH elements before silencing them, so teardown
      // pause/src-reset events never reach the engine or failure budget.
      router.detachAll();
      mixer.dispose();
      audioRef.current = null;
      mixerRef.current = null;
    };
  }, [advance, debugMetric, report, requestForceResync]);

  // ---- multi-room drift correction loop ----

  // Every DRIFT_TICK_INTERVAL_MS while scheduled music is on air with a sync
  // timeline: measure element position vs the server timeline (on the
  // clock-offset-corrected wall clock) and apply the pure policy decision —
  // gentle rate nudge inside the soft/hard band, hard re-seek beyond it.
  // Decisions live in drift-loop.ts; this effect is only the timer + applier.
  useEffect(() => {
    const timer = window.setInterval(() => {
      const s = syncRef.current;
      const cur = currentRef.current;
      const mixer = mixerRef.current;
      const el = mixer?.getCurrentElement() ?? audioRef.current;
      const eligible = isDriftTickEligible({
        hasTimeline: hasSyncTimeline(s),
        clockSynced: clockSyncedRef.current,
        reason: cur?.reason ?? null,
        interrupt: cur?.interrupt ?? false,
        isPlaying: isPlayingRef.current,
        broadcastActive: broadcastActiveRef.current,
        crossfading: mixer?.isCrossfading ?? false,
      });
      if (!eligible || !el || !cur) {
        lastDriftSecRef.current = null;
        // A stale count across an eligibility gap would defeat the debounce:
        // one leftover tick plus the first tick after the gap escalates
        // immediately.
        wrongItemTicksRef.current = 0;
        resetDriftNudge();
        return;
      }

      const now = correctedNowDate();
      // Operator override grace: the wrong-item count is pinned at zero so
      // the escalation cannot fire against a track the operator just chose.
      const manualGrace = Date.now() < manualOverrideUntilRef.current;
      const decision = driftLoopTick({
        sync: s,
        itemId: cur.item.id,
        positionSec: el.currentTime,
        now,
        wasNudging: driftWasNudgingRef.current,
        rebase: seekRebaseRef.current,
        wrongItemTicks: manualGrace ? 0 : wrongItemTicksRef.current,
      });
      lastDriftSecRef.current = decision.driftSec;
      driftWasNudgingRef.current = decision.nudging;
      wrongItemTicksRef.current = manualGrace ? 0 : decision.wrongItemTicks;

      switch (decision.action.kind) {
        case "nudge":
          driftNudgeAppliedRef.current = true;
          if (el.playbackRate !== decision.action.rate) {
            el.playbackRate = decision.action.rate;
          }
          break;
        case "seek": {
          resetDriftNudge();
          const alignment = alignMusicToTimeline(
            s,
            musicRotationRef.current.map((item) => item.id),
            now,
          );
          if (!alignment) break;
          debugMetric("sync.drift-seek", {
            driftSec: decision.driftSec,
            seekSec: alignment.seekSec,
            itemId: alignment.segment.itemId,
          });
          const seekPlan = planDriftSeek({
            alignment,
            currentItemId: cur.item.id,
            queuedInterrupts: interruptQueueRef.current.length,
          });
          if (seekPlan.kind === "seek-element") {
            el.currentTime = seekPlan.seekSec;
          } else if (seekPlan.kind === "rejoin") {
            applyMusicTimelineAlignment(now);
            advance();
          }
          break;
        }
        default:
          // Back inside the release band: restore normal rate if we nudged.
          resetDriftNudge();
          break;
      }
    }, DRIFT_TICK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [
    advance,
    applyMusicTimelineAlignment,
    correctedNowDate,
    debugMetric,
    resetDriftNudge,
  ]);

  // ---- controls ----

  // av_full foregrounds autoplay UNMUTED, which browsers commonly block; the
  // autoPlay attribute swallows the rejection and the takeover freezes on its
  // first frame. Play explicitly: on a policy rejection fall back to muted
  // playback so the visuals keep running, and surface the existing
  // tap-to-start recovery — its gesture path retries unmuting (see
  // retryForegroundPlayback). Non-policy rejections (teardown races, media
  // errors) are left to the element's error path.
  const ensureForegroundPlayback = useCallback(
    (el: HTMLVideoElement) => {
      void el.play().catch((err: unknown) => {
        const blocked =
          err instanceof DOMException && err.name === "NotAllowedError";
        if (!blocked) return;
        el.muted = true;
        foregroundAutoplayMutedRef.current = true;
        void el.play().catch(() => {});
        setAutoplayBlocked(true);
        debugMetric("video.autoplay-blocked", { surface: "foreground" });
      });
    },
    [debugMetric],
  );

  // Gesture-time recovery for the foreground video: restore its intended
  // mute state (ensureForegroundPlayback muted it when unmuted autoplay was
  // blocked) and kick playback if it never got going. play() is also driven
  // by remote transport commands — not real gestures — so if unmuting gets
  // rejected again, fall back to muted so the visuals keep running.
  const retryForegroundPlayback = useCallback(() => {
    const el = foregroundVideoElRef.current;
    if (!el || !foregroundAutoplayMutedRef.current) return;
    const intendedMuted =
      foregroundVideoMutedRef.current ||
      broadcastActiveRef.current ||
      screenDuckedRef.current;
    el.muted = intendedMuted;
    if (!intendedMuted) foregroundAutoplayMutedRef.current = false;
    void el.play().then(
      // A schedule can be visual-only: no audio entry ever plays to clear
      // the overlay, so a recovered foreground clears it itself — but ONLY
      // in that visual-only case. When audio is on air its own play() promise
      // owns the overlay; clearing here would race it (the video's muted
      // fallback resolves even while the audio bus is still blocked) and
      // hide the tap-to-start prompt on a silent venue.
      () => {
        if (!currentRef.current) setAutoplayBlocked(false);
      },
      () => {
        el.muted = true;
        foregroundAutoplayMutedRef.current = true;
        void el.play().catch(() => {});
      },
    );
  }, []);

  // Resolves the REAL outcome so remote play commands ack honestly: on an
  // autoplay-restricted kiosk audio.play() rejects, and the operator console
  // must see that failure instead of an optimistic success (see
  // applyPlayerCommand in player-core).
  const play = useCallback(async (): Promise<PlayOutcome> => {
    // Resuming the paused bed at full gain over a live announcement is the
    // exact takeover the broadcast pause exists to prevent; report the block
    // instead (setBroadcastActive(false) resumes when the mic closes).
    const blocked = liveAnnouncementGuard(broadcastActiveRef.current);
    if (blocked) return blocked;
    // The operator asked for sound: from here the schedule may start entries
    // again (a refused command above deliberately leaves the intent alone).
    wantPlayingRef.current = true;
    await mixerRef.current?.resumeContext();
    // The user gesture also unlocks the screen bus: retry the foreground
    // video unmuted (autoplay recovery may have muted it as a fallback).
    retryForegroundPlayback();
    const audio = audioRef.current;
    if (!audio) return { ok: false, reason: t("playerUi.playback.notReady") };
    if (!currentRef.current) {
      // Nothing on air yet: kick the engine, propagating advance()'s
      // synchronous refusals (nothing scheduled to pull). When it does start
      // an entry its ok stays optimistic for the genuinely-async part only:
      // playEntry's own start path reports autoplay failures via
      // autoplayBlocked/status.
      return advance();
    }
    try {
      await audio.play();
      setAutoplayBlocked(false);
      setStallWarning(null);
      scheduleStallCheck(audio);
      debugMetric("play.gesture", {
        ctxState: mixerRef.current?.getContextState() ?? null,
      });
      return { ok: true };
    } catch (err) {
      setIsPlaying(false);
      setAutoplayBlocked(true);
      debugMetric("play.gesture-denied", {
        message: err instanceof Error ? err.message : String(err),
      });
      const blocked =
        err instanceof DOMException && err.name === "NotAllowedError";
      return {
        ok: false,
        reason: blocked
          ? t("playerUi.playback.remotePlayBlocked")
          : t("playerUi.playback.failed", {
              message: err instanceof Error ? err.message : String(err),
            }),
      };
    }
  }, [advance, debugMetric, retryForegroundPlayback, scheduleStallCheck]);

  const pause = useCallback(() => {
    // Sticky: the schedule keeps running (boundaries, timed fires, resyncs all
    // still load what should be on air) but nothing sounds again until an
    // operator play. Without this the very next ad fire or track boundary
    // restarted the venue seconds after the operator pressed pause.
    wantPlayingRef.current = false;
    // Through the mixer when present: it snaps any in-flight crossfade to its
    // end state and pauses BOTH elements — pausing only audioRef mid-fade
    // would silence the outgoing leg while the incoming one keeps rising.
    const mixer = mixerRef.current;
    if (mixer) mixer.pause();
    else audioRef.current?.pause();
  }, []);

  const stop = useCallback(() => {
    // Same stickiness as pause: a stopped player stays stopped until told
    // otherwise, instead of being restarted by the next scheduled transition.
    wantPlayingRef.current = false;
    playEntry(null);
  }, [playEntry]);

  const next = useCallback((): PlayOutcome => {
    return advance({ manual: true });
  }, [advance]);

  const playPrevious = useCallback((): PlayOutcome => {
    const blocked = liveAnnouncementGuard(broadcastActiveRef.current);
    if (blocked) return blocked;
    const prev = historyRef.current.pop();
    if (!prev) return { ok: false, reason: "historyEmpty" };
    setPreviousEntry(historyRef.current[historyRef.current.length - 1] ?? null);
    if (currentRef.current) {
      interruptQueueRef.current.unshift(currentRef.current);
    }
    playEntry(prev, { manual: true });
    historyRef.current.pop();
    return { ok: true };
  }, [playEntry]);

  const setVolume = useCallback(
    (v: number) => {
      const clamped = Math.max(0, Math.min(100, Math.round(v)));
      volumeRef.current = clamped;
      setVolumeState(clamped);
      // Recompute through the centralized gain so the per-schedule slot volume
      // and any active duck stay composed with the new global level.
      applyAudioGain();
      scheduleReport();
    },
    [applyAudioGain, scheduleReport],
  );

  const setMuted = useCallback(
    (next: boolean) => {
      mutedRef.current = next;
      setMutedState(next);
      applyAudioGain();
      scheduleReport();
    },
    [applyAudioGain, scheduleReport],
  );

  const toggleMute = useCallback(() => {
    setMuted(!mutedRef.current);
  }, [setMuted]);

  const shuffleNow = useCallback(() => {
    if (musicRotationRef.current.length > 1) {
      musicRotationRef.current = seededShuffle(
        musicRotationRef.current,
        `manual:${Date.now()}`,
      );
      musicIndexRef.current = 0;
    }
    advance({ manual: true });
  }, [advance]);

  const setScheduleEnabled = useCallback(
    (on: boolean) => {
      scheduleEnabledRef.current = on;
      setScheduleEnabledState(on);
      if (!on) {
        playEntry(null);
      } else {
        advance();
      }
    },
    [advance, playEntry],
  );

  const setBroadcastActive = useCallback(
    (active: boolean) => {
      if (broadcastActiveRef.current === active) return;
      broadcastActiveRef.current = active;
      const audio = audioRef.current;
      // While a live announcement is on air, silence every other channel: pause
      // the audio bus and mute the video bus' audio (the picture keeps playing).
      // Mixer pause also snaps an in-flight crossfade so both legs go silent.
      if (active) {
        if (mixerRef.current) mixerRef.current.pause();
        else audio?.pause();
        if (videoElRef.current) videoElRef.current.muted = true;
        if (foregroundVideoElRef.current) {
          foregroundVideoElRef.current.muted = true;
        }
      } else {
        // Announcement ended: restore the video bus to its configured audio
        // (applyScreenAudio honors the configured mute and any active duck on
        // both the signage and foreground video elements)…
        applyScreenAudioRef.current();
        // …and resume the scheduled audio program if scheduling is on AND the
        // operator still wants sound. A live announcement ending must not
        // un-pause a venue that was deliberately paused before it started.
        if (scheduleEnabledRef.current) {
          timedSchedulerRef.current?.flushBroadcastQueue();
          if (currentRef.current && audio?.src && !bedPausedForTakeoverRef.current) {
            if (wantPlayingRef.current) {
              void audio.play().catch(() => setIsPlaying(false));
            }
          } else if (!currentRef.current) {
            advance();
          }
        }
      }
    },
    [advance],
  );

  const playMediaItemNow = useCallback(
    (item: DeviceMediaItem, channelType?: ChannelType): PlayOutcome => {
      if (item.type === "video" || item.type === "image") {
        // Playing a new visual explicitly un-blanks a paused screen.
        if (screenPausedRef.current) {
          screenPausedRef.current = false;
          setScreenPaused(false);
        }
        clearForegroundVisual();
        videoImageDisplaySecRef.current = null;
        // Manual one-offs never loop: onended returns to the scheduled
        // rotation via advanceVideo.
        applyVideoLoop(false);
        setVideoItem(item);
        setNextVideoItem(null);
        setVideoPlaylistName(t("player.libraryLabel"));
        return { ok: true };
      }
      // play_now on the audio bus must not stomp a live announcement; the
      // screen-bus branch above stays free.
      const blocked = liveAnnouncementGuard(broadcastActiveRef.current);
      if (blocked) return blocked;
      // Operator "play this now" is a manual transport action → hard cut, and
      // it carries its own intent to sound (it lifts a pause).
      playEntry(manualEntryFromMediaItem(item, channelType), {
        manual: true,
        autoPlay: true,
      });
      return { ok: true };
    },
    [applyVideoLoop, clearForegroundVisual, playEntry],
  );

  // Start a fully-resolved playlist now (cut the current item; the schedule
  // resumes after via advance → takeNextMusic). The playlist may come from the
  // device sync or be fetched on demand for non-scheduled library playlists.
  const startPlaylist = useCallback(
    (playlist: DevicePlaylist): PlayOutcome => {
      const shuffleBase = syncShuffleBase(syncRef.current);
      if (playlist.channelType === "video") {
        const signage = buildRotation(playlist, shuffleBase).filter(isSignage);
        // An empty signage rotation is a no-op: report it instead of clearing
        // whatever is on screen and leaving the operator guessing.
        if (signage.length === 0) return { ok: false, reason: "emptyPlaylist" };
        videoPlaylistIdRef.current = playlist.id;
        videoRotationRef.current = signage;
        videoIndexRef.current = 0;
        videoImageDisplaySecRef.current = null;
        applyVideoLoop(shouldLoopVideo(signage.length));
        setVideoPlaylistName(playlist.name);
        clearForegroundVisual();
        setVideoItem(signage[0] ?? null);
        setNextVideoItem(peekNextVideo());
        return { ok: true };
      }

      // Audio-bus playlists cut the current item in; a live announcement
      // holds that bus (the signage branch above stays free).
      const blocked = liveAnnouncementGuard(broadcastActiveRef.current);
      if (blocked) return blocked;

      const slot = channelSlot(syncRef.current, playlist.channelType);
      const items = buildRotation(playlist, shuffleBase);
      if (items.length === 0) return { ok: false, reason: "emptyPlaylist" };
      const reason = playReasonForChannel(playlist.channelType);
      // origin:"manual": the operator picked this playlist over the schedule,
      // so soft resync must not measure it against the scheduled slot — an
      // unrelated content edit used to cut it mid-track (and drop the queued
      // remainder when the playlist wasn't in the device payload at all).
      const entries = items.map((it) => ({
        ...makeEntry(it, reason, slot, playlist, reason !== "music"),
        origin: "manual" as const,
      }));
      const [first, ...rest] = entries;
      interruptQueueRef.current.unshift(...rest);
      refreshQueueUi();
      // Manual: this is play-now for a whole playlist. Music entries here are
      // NOT interrupts, so without the grace the group timeline's wrong-item
      // rejoin cut the operator's playlist as soon as the queue drained.
      playEntry(first, { manual: true, autoPlay: true });
      return { ok: true };
    },
    [clearForegroundVisual, peekNextVideo, playEntry, refreshQueueUi],
  );

  // Resolve a playlist for playback: prefer the local sync (already hydrated),
  // otherwise fetch it from the device API so arbitrary library playlists (not
  // referenced by the schedule, hence absent from sync) still play.
  const resolvePlaylistForPlayback = useCallback(
    async (playlistId: string): Promise<DevicePlaylist | null> =>
      playlistById(syncRef.current, playlistId) ??
      (await resolveDevicePlaylist(playlistId)),
    [],
  );

  const playPlaylistNow = useCallback(
    (playlistId: string) => {
      const playlist = playlistById(syncRef.current, playlistId);
      if (playlist) startPlaylist(playlist);
    },
    [startPlaylist],
  );

  const resolveMediaRef = useCallback(
    async (ref: MediaRefArgs): Promise<DeviceMediaItem | null> => {
      if (ref.playlistId) return null;
      if (!ref.source) return null;
      return resolveDeviceMedia({
        source: ref.source,
        catalogueTrackId: ref.catalogueTrackId,
        mediaAssetId: ref.mediaAssetId,
      });
    },
    [],
  );

  const playMediaRef = useCallback(
    async (ref: MediaRefArgs): Promise<boolean | PlayOutcome> => {
      if (ref.playlistId) {
        const playlist = await resolvePlaylistForPlayback(ref.playlistId);
        if (!playlist) return false;
        // Propagate the outcome so a resolvable-but-empty playlist reports
        // "emptyPlaylist" instead of an optimistic ok.
        return startPlaylist(playlist);
      }
      const item = await resolveMediaRef(ref);
      if (!item) return false;
      // Propagate the outcome so a blocked start (a live announcement holds
      // the audio bus) reports its reason instead of an optimistic ok.
      return playMediaItemNow(item, ref.channelType);
    },
    [playMediaItemNow, resolveMediaRef, resolvePlaylistForPlayback, startPlaylist],
  );

  const addMediaRefToQueue = useCallback(
    async (ref: MediaRefArgs): Promise<boolean> => {
      if (ref.playlistId) {
        const playlist = await resolvePlaylistForPlayback(ref.playlistId);
        if (!playlist || playlist.channelType === "video") return false;
        const slot = channelSlot(syncRef.current, playlist.channelType);
        const shuffleBase = syncShuffleBase(syncRef.current);
        const items = buildRotation(playlist, shuffleBase);
        if (items.length === 0) return false;
        const reason = playReasonForChannel(playlist.channelType);
        const entries = items.map((it) =>
          makeEntry(it, reason, slot, playlist, true),
        );
        interruptQueueRef.current.push(...entries);
        refreshQueueUi();
        report(currentTransportState());
        return true;
      }
      const item = await resolveMediaRef(ref);
      if (!item || item.type === "video" || item.type === "image") return false;
      interruptQueueRef.current.push(manualEntryFromMediaItem(item, ref.channelType));
      refreshQueueUi();
      report(currentTransportState());
      return true;
    },
    [
      currentTransportState,
      refreshQueueUi,
      report,
      resolveMediaRef,
      resolvePlaylistForPlayback,
    ],
  );

  const removeFromQueue = useCallback(
    (index: number) => {
      if (index < 0 || index >= interruptQueueRef.current.length) return;
      interruptQueueRef.current.splice(index, 1);
      refreshQueueUi();
      report(currentTransportState());
    },
    [currentTransportState, refreshQueueUi, report],
  );

  const reorderQueue = useCallback(
    (fromIndex: number, toIndex: number) => {
      const q = interruptQueueRef.current;
      if (
        fromIndex < 0 ||
        fromIndex >= q.length ||
        toIndex < 0 ||
        toIndex >= q.length ||
        fromIndex === toIndex
      ) {
        return;
      }
      const [moved] = q.splice(fromIndex, 1);
      if (moved) q.splice(toIndex, 0, moved);
      refreshQueueUi();
      report(currentTransportState());
    },
    [currentTransportState, refreshQueueUi, report],
  );

  const getQueueSnapshot = useCallback(() => {
    const current = currentRef.current;
    return {
      queue: interruptQueueRef.current.map(toQueueSnapshot),
      current: current ? toQueueSnapshot(current) : null,
    };
  }, []);

  const seek = useCallback(
    (positionSec: number) => {
      const audio = audioRef.current;
      if (!audio || !currentRef.current) return;
      const max =
        Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
      const clamped =
        max > 0 ? Math.max(0, Math.min(max, positionSec)) : Math.max(0, positionSec);
      audio.currentTime = clamped;
      setPositionSec(Math.floor(clamped));
      // Re-baseline drift correction on the operator's intent. The server fans
      // this same seek out to every device in the sync group, so each derives
      // an identical rebase and the group moves TOGETHER rather than one device
      // drifting away from the others.
      const item = currentRef.current?.item.id;
      seekRebaseRef.current = item
        ? { itemId: item, positionSec: clamped, atMs: correctedNowDate().getTime() }
        : null;
      report(audio.paused ? "paused" : "playing");
    },
    [report, correctedNowDate],
  );

  const resync = useCallback(() => {
    musicPlaylistIdRef.current = null;
    adRotationStateRef.current = createLocalRotationState();
    announcementPlaylistIdRef.current = null;
    interruptQueueRef.current = [];
    visualEventQueueRef.current = [];
    clearForegroundVisual();
    refreshQueueUi();
    applyMusicTimelineAlignment();
    syncVideo();
    advance();
  }, [
    advance,
    applyMusicTimelineAlignment,
    clearForegroundVisual,
    refreshQueueUi,
    syncVideo,
  ]);

  // ---- persisted output level (device settings) ----

  // Master volume/mute persist in device settings (written server-side when an
  // operator dispatches set_volume/set_mute, and by the display-settings
  // PATCH). Apply them when the delivered settings VALUE changes: the first
  // sync restores the operator's level after a reload/reboot, and later
  // refetches only re-apply on a real edit — so a payload carrying unchanged
  // settings never stomps a live remote adjustment made moments earlier.
  const lastSettingsVolumeRef = useRef<number | null>(null);
  const lastSettingsMutedRef = useRef<boolean | null>(null);
  // Stamped by the command layer when a remote set_volume/set_mute lands:
  // for a short shield window, settings payloads still update the refs (so a
  // later real edit is detected) but never re-apply — a stale in-flight sync
  // fetched before the server persisted the command can't stomp the level.
  const remoteOutputChangeAtRef = useRef(0);
  const noteRemoteOutputChange = useCallback(() => {
    remoteOutputChangeAtRef.current = Date.now();
  }, []);
  useEffect(() => {
    const settings = sync?.device?.settings;
    if (!settings) return;
    const persistedVolume = readOutputVolume(settings);
    const persistedMuted = readOutputMuted(settings);
    const shielded =
      Date.now() - remoteOutputChangeAtRef.current < REMOTE_OUTPUT_SHIELD_MS;
    if (lastSettingsVolumeRef.current !== persistedVolume) {
      lastSettingsVolumeRef.current = persistedVolume;
      if (!shielded) setVolume(persistedVolume);
    }
    if (lastSettingsMutedRef.current !== persistedMuted) {
      lastSettingsMutedRef.current = persistedMuted;
      if (!shielded) setMuted(persistedMuted);
    }
  }, [sync, setVolume, setMuted]);

  const lastSyncVersionRef = useRef<string | null>(null);

  useEffect(() => {
    syncRef.current = sync;
    if (!sync) return;

    // Presigned URLs rotate on every fetch while syncVersion fingerprints
    // content only, so a same-version payload still carries fresh URLs. Swap
    // the item objects cached in rotations and the interrupt queue for their
    // re-signed counterparts — otherwise a long-lived tab keeps serving the
    // URLs it captured when the rotation was first built, until they expire.
    // (Ads/announcements no longer cache an array — nextRotationItem reads
    // playlist.items straight off the current sync, so they're always fresh.)
    const freshItemById = collectSyncItemsById(sync);
    musicRotationRef.current = remapItemsById(
      musicRotationRef.current,
      freshItemById,
    );
    videoRotationRef.current = remapItemsById(
      videoRotationRef.current,
      freshItemById,
    );
    interruptQueueRef.current = interruptQueueRef.current.map((entry) => {
      const fresh = freshItemById.get(entry.item.id);
      return fresh && fresh !== entry.item ? { ...entry, item: fresh } : entry;
    });

    const version = sync.syncVersion ?? null;
    const prevVersion = lastSyncVersionRef.current;
    lastSyncVersionRef.current = version;

    // A version change fingerprints the device's content as a whole, not the
    // playing channel's. Apply it softly (planSoftResync): drop only the
    // queued interrupts the payload no longer carries, refresh the music
    // rotation in place for same-playlist edits, and hard-cut the audio bus
    // below only when the on-air entry did not survive (slot swapped or
    // emptied, item removed, channel off). The per-channel controllers detect
    // their own occupancy changes from the payload, so nothing else needs a
    // forced reset here; the operator's explicit resync command keeps the
    // full hard reset (resync()).
    let cutAudio = false;
    if (prevVersion && version && prevVersion !== version) {
      const plan = planSoftResync(
        sync,
        currentRef.current,
        interruptQueueRef.current,
      );
      interruptQueueRef.current = plan.queue;
      refreshQueueUi();
      const refresh = planMusicRotationRefresh(
        sync,
        {
          rotation: musicRotationRef.current,
          playlistId: musicPlaylistIdRef.current,
        },
        currentRef.current?.item.id,
      );
      if (refresh) {
        musicRotationRef.current = refresh.rotation;
        musicIndexRef.current = refresh.index;
        musicRotationAnchorRef.current = {
          consumedItemId: refresh.consumedItemId,
        };
      }
      cutAudio = !plan.keepCurrent;
    }

    applyMusicTimelineAlignment();
    syncVideo();

    if (!scheduleEnabledRef.current) return;

    const interrupted = syncAnnouncements();

    // Same standing-intent gate as every other restart path (shared core with
    // the native shell). A paused player used to have the entry it was paused
    // ON swapped out from under it here: an announcement slot change (or a
    // content cut) ran advance(), which silently loaded the announcement over
    // that track — so the console named an item the operator never chose and
    // resuming played it. An EMPTY bus still advances: that silent load is how
    // a paused player keeps tracking the schedule.
    if (
      shouldRestartOnSync({
        contentCut: cutAudio,
        interrupted,
        busIdle: !currentRef.current,
        wantPlaying: wantPlayingRef.current,
      })
    ) {
      advance();
    }

    checkScheduleMismatch();
    reportScheduledChannels();
  }, [
    sync,
    refreshQueueUi,
    syncVideo,
    syncAnnouncements,
    advance,
    applyMusicTimelineAlignment,
    checkScheduleMismatch,
    reportScheduledChannels,
  ]);

  const setVideoElement = useCallback(
    (el: HTMLVideoElement | null) => {
      const prev = videoElRef.current;
      if (prev) {
        prev.onended = null;
        prev.onerror = null;
        prev.onplaying = null;
      }
      videoElRef.current = el;
      if (el) {
        // The element mounts after the state change that decided the loop
        // policy — read the recorded decision (see applyVideoLoop).
        el.loop = videoLoopRef.current;
        // Never leave a finished element on air (see shouldReplayAttachedVideo):
        // the crossfade can hand back the very element that just ended, and
        // React re-attaches this ref instead of remounting it.
        if (shouldReplayAttachedVideo(el)) replayVideoElement(el);
        el.onended = () => advanceVideo();
        el.onerror = () => handleScreenVideoError("signage");
        el.onplaying = () => noteScreenPlaybackHealthy();
      }
    },
    [advanceVideo, handleScreenVideoError, noteScreenPlaybackHealthy],
  );

  const setForegroundVideoElement = useCallback(
    (el: HTMLVideoElement | null) => {
      const prev = foregroundVideoElRef.current;
      if (prev) {
        prev.onended = null;
        prev.onerror = null;
        prev.onplaying = null;
      }
      foregroundVideoElRef.current = el;
      // A fresh element carries the intended muted prop again; any previous
      // autoplay-fallback mute died with the old element.
      foregroundAutoplayMutedRef.current = false;
      if (el) {
        el.onended = () => advanceVisualEvent();
        el.onerror = () => handleScreenVideoError("foreground");
        el.onplaying = () => noteScreenPlaybackHealthy();
        ensureForegroundPlayback(el);
      }
    },
    [
      advanceVisualEvent,
      ensureForegroundPlayback,
      handleScreenVideoError,
      noteScreenPlaybackHealthy,
    ],
  );

  // ---- screen-bus (video) audio ----

  const applyScreenAudio = useCallback(() => {
    // A live announcement (broadcast) or a `muteOthers` audio slot takes over:
    // force the video bus silent for the duration, regardless of the configured
    // screen mute/volume.
    const forceMute =
      screenMutedRef.current ||
      broadcastActiveRef.current ||
      screenDuckedRef.current;
    const vol = forceMute ? 0 : screenVolumeRef.current / 100;
    const fg = foregroundVideoElRef.current;
    if (fg) {
      fg.muted = forceMute;
      fg.volume = vol;
    }
    if (videoElRef.current) {
      videoElRef.current.muted = forceMute;
    }
  }, []);

  // Ref so the earlier-declared setBroadcastActive can restore screen audio
  // without a forward reference / dependency-array TDZ.
  const applyScreenAudioRef = useRef(applyScreenAudio);
  applyScreenAudioRef.current = applyScreenAudio;
  // Same indirection for recomputeDucking, which is declared before this.
  applyScreenAudioRef2.current = applyScreenAudio;

  const setScreenVolume = useCallback(
    (v: number) => {
      screenVolumeRef.current = Math.max(0, Math.min(100, Math.round(v)));
      applyScreenAudio();
      scheduleReport();
    },
    [applyScreenAudio, scheduleReport],
  );

  const setScreenMuted = useCallback(
    (next: boolean) => {
      screenMutedRef.current = next;
      applyScreenAudio();
      scheduleReport();
    },
    [applyScreenAudio, scheduleReport],
  );

  // ---- per-channel transport (two-bus addressing) ----

  // Apply a transport action to a single channel, routed to its bus. Audio-bus
  // channels share one element: starting a non-active audio channel supersedes
  // whatever audio was playing (and resumes via the interrupt/advance chain when
  // it finishes); the screen bus is driven independently so audio + video can
  // play at once.
  const startAudioChannelNow = useCallback(
    (channelType: ChannelType): PlayOutcome => {
      // Starting any audio channel cuts onto the shared audio element, which
      // a live announcement holds exclusively.
      const blocked = liveAnnouncementGuard(broadcastActiveRef.current);
      if (blocked) return blocked;
      let entry: QueueEntry | null = null;
      if (channelType === "ads") entry = takeNextAd();
      else if (channelType === "announcements") entry = takeNextAnnouncement();
      else entry = takeNextMusic();
      // Nothing to start (inactive slot / empty rotation): tell the caller so
      // the remote command acks "nothingScheduled" instead of ok.
      if (!entry) return { ok: false, reason: "nothingScheduled" };
      // Operator-started announcements are a takeover too: play the attention
      // tone first, then the announcement from the interrupt queue on `ended`.
      if (channelType === "announcements") {
        const tone = readAnnouncementTone(syncRef.current?.device?.settings);
        const toneSrc = resolveToneUrl(
          syncRef.current?.device?.settings,
          syncRef.current?.device?.announcementToneUrl,
        );
        if (
          tone &&
          toneSrc &&
          shouldPrependTone(
            tone,
            currentRef.current?.item.id,
            interruptQueueRef.current[0]?.item.id,
          )
        ) {
          interruptQueueRef.current.unshift(entry);
          playEntry(toneQueueEntry(tone, toneSrc), {
            manual: true,
            autoPlay: true,
          });
          return { ok: true };
        }
      }
      // Manual: the operator started this channel, so it outranks the group
      // timeline for the grace window like any other hand-picked entry.
      playEntry(entry, { manual: true, autoPlay: true });
      return { ok: true };
    },
    [playEntry, takeNextAd, takeNextAnnouncement, takeNextMusic],
  );

  // Every path resolves a PlayOutcome: a transport that would previously
  // no-op silently (pausing an idle channel, skipping during a takeover, …)
  // now reports { ok: false, reason } so the operator's console can say why
  // nothing happened instead of showing a false success.
  const controlChannel = useCallback(
    (
      channelType: ChannelType,
      action: ChannelTransport,
    ): PlayOutcome | Promise<PlayOutcome> => {
      // No grace arming here: it belongs to playEntry's `manual` seam, which
      // fires only once an action is actually accepted. Arming at this entry
      // point pinned the MUSIC drift loop for 90s on a signage skip and on
      // commands the player then refused.
      if (busForChannel(channelType) === "screen") {
        const el = foregroundVideoElRef.current ?? videoElRef.current;
        const audio = audioRef.current;
        // During full-AV the video channel also drives the shared audio element,
        // so transport must move both surfaces together (otherwise pausing the
        // video would leave its sound playing on the audio element).
        const targets = transportTargets(
          channelType,
          currentRef.current?.channelType ?? null,
        );
        switch (action.type) {
          case "play": {
            // Resume from a blanked (paused) signage screen: un-blank and let
            // the rotation re-arm. Works even when there is no <video> element.
            if (screenPausedRef.current) {
              screenPausedRef.current = false;
              setScreenPaused(false);
            }
            void el?.play().catch(() => {});
            if (targets.audioElement) {
              const outcome = play();
              scheduleReport();
              return outcome;
            }
            scheduleReport();
            return { ok: true };
          }
          case "pause":
            // Freeze any real video element. Signage (web page / menu / widget /
            // still image) has no <video> to pause, so per the owner's decision
            // a pause BLANKS the screen and halts the rotation instead of being
            // rejected (CB-143). Never a silent no-op now.
            foregroundVideoElRef.current?.pause();
            videoElRef.current?.pause();
            if (targets.audioElement) pause();
            if (!el) {
              screenPausedRef.current = true;
              setScreenPaused(true);
            }
            scheduleReport();
            return { ok: true };
          case "next":
          case "previous": {
            // advanceVideo drops the request while a takeover holds the
            // screen; surface that instead of a silent no-op.
            if (takeoverActiveRef.current) {
              return { ok: false, reason: "takeoverActive" };
            }
            // Advancing the screen implies resuming it.
            if (screenPausedRef.current) {
              screenPausedRef.current = false;
              setScreenPaused(false);
            }
            // With no video element and nothing in the screen rotation this
            // would be a silent no-op — report it instead of acking success.
            if (!el && videoRotationRef.current.length === 0 && !targets.audioElement) {
              return { ok: false, reason: "noScreenMedia" };
            }
            advanceVideo();
            const audioOutcome = targets.audioElement
              ? advance({ manual: true })
              : null;
            scheduleReport();
            return audioOutcome ?? { ok: true };
          }
          case "seek":
            if (!el && !(targets.audioElement && audio)) {
              return { ok: false, reason: "noScreenMedia" };
            }
            if (el) el.currentTime = Math.max(0, action.positionSec);
            if (targets.audioElement && audio) {
              audio.currentTime = Math.max(0, action.positionSec);
            }
            scheduleReport();
            return { ok: true };
          case "setVolume":
            setScreenVolume(action.volume);
            return { ok: true };
          case "setMute":
            setScreenMuted(action.muted);
            return { ok: true };
          default:
            return { ok: false, reason: "notHandled" };
        }
      }

      // Audio bus.
      const current = currentRef.current;
      const activeAudioChannel =
        current && busForChannel(current.channelType) === "audio"
          ? current.channelType
          : null;
      const isActive = activeAudioChannel === channelType;
      switch (action.type) {
        case "play":
          // Propagate the real outcome for the on-air channel; starting an
          // idle channel reports "nothingScheduled" when its slot has nothing
          // playable right now, or "liveAnnouncementActive" while a live
          // broadcast holds the audio bus.
          if (isActive) return play();
          return startAudioChannelNow(channelType);
        case "pause":
          if (!isActive) return { ok: false, reason: "channelNotActive" };
          pause();
          return { ok: true };
        case "next":
          if (isActive) return advance({ manual: true });
          return startAudioChannelNow(channelType);
        case "previous":
          if (!isActive) return { ok: false, reason: "channelNotActive" };
          return playPrevious();
        case "seek":
          if (!isActive) return { ok: false, reason: "channelNotActive" };
          seek(action.positionSec);
          return { ok: true };
        case "setVolume":
          setVolume(action.volume);
          return { ok: true };
        case "setMute":
          setMuted(action.muted);
          return { ok: true };
        default:
          return { ok: false, reason: "notHandled" };
      }
    },
    [
      advance,
      advanceVideo,
      pause,
      play,
      playPrevious,
      scheduleReport,
      seek,
      setMuted,
      setScreenMuted,
      setScreenVolume,
      setVolume,
      startAudioChannelNow,
    ],
  );

  useEffect(() => {
    if (!isPlayerDebugEnabled()) return;
    const refresh = () => {
      setDebugAudioSnapshot(snapshotAudioElement(audioRef.current));
      setDebugAudioContextState(mixerRef.current?.getContextState() ?? null);
    };
    refresh();
    const id = window.setInterval(refresh, 1000);
    return () => clearInterval(id);
  }, []);

  return {
    current,
    previousEntry,
    upNext,
    queuePreview,
    isPlaying,
    autoplayBlocked,
    positionSec,
    durationSec,
    volume,
    muted,
    scheduleEnabled,
    videoItem,
    nextVideoItem,
    signageContent,
    videoPlaylistName,
    foregroundVideoItem,
    foregroundVideoMuted,
    foregroundImageDisplaySec,
    screenPaused,
    playerDebug: {
      audioSnapshot: debugAudioSnapshot,
      audioContextState: debugAudioContextState,
      stallWarning,
    },
    play,
    pause,
    stop,
    next,
    previous: playPrevious,
    setVolume,
    toggleMute,
    setMuted,
    noteRemoteOutputChange,
    shuffleNow,
    setScheduleEnabled,
    setBroadcastActive,
    playPlaylistNow,
    playMediaRef,
    addMediaRefToQueue,
    removeFromQueue,
    reorderQueue,
    getQueueSnapshot,
    resync,
    seek,
    controlChannel,
    setVideoElement,
    setForegroundVideoElement,
    // A still image has no media element, so its <img> load error is routed
    // here to run the same screen failure budget → skip/resync as video
    // (CB-122): telemetry + advance past the broken still.
    reportScreenImageError: handleScreenVideoError,
  };
}
